import {
  type LanguageLocaleEnum,
  Translation,
  TranslationCreate,
} from "../api/openapi";
import { CollectionType } from "./types";

type Keyword = {
  [T in LanguageLocaleEnum]: string;
};

type Keywords = {
  [T in string]: Keyword;
};

const generalKeywords = {
  home: {
    de: "Home",
    it: "Home",
    en: "Home",
  },
  files: {
    de: "Dateien",
    it: "Documenti",
    en: "Files",
  },
  upload: {
    de: "Hochladen",
    it: "Carica",
    en: "Upload",
  },
  circularLetters: {
    de: "Rundschreiben",
    it: "Circolari",
    en: "Circular letters",
  },
  calendar: {
    de: "Kalender",
    it: "Calendario",
    en: "Calendar",
  },
  contactPerson: {
    de: "Ansprechpartner",
    it: "Persona di riferimento",
    en: "Contact person",
  },
  contact: {
    de: "Kontakt",
    it: "Contatto",
    en: "Contact",
  },
  yourAccountingBranch: {
    de: "Ihre Buchhaltungsfiliale",
    it: "La Sua filiale della contabilità",
    en: "Your accounting branch",
  },
  yourPayrollBranch: {
    de: "Ihre Löhnefiliale",
    it: "La Sua filiale dei salari",
    en: "Your Payroll branch",
  },
  yourBranch: {
    de: "Ihre Filiale",
    it: "La Sua filiale",
    en: "Your branch",
  },
  messages: {
    de: "Nachrichten",
    it: "Messaggi",
    en: "Messages",
  },
  communication: {
    de: "Kommunikation",
    it: "Comunicazione",
    en: "Communication",
  },
  administration: {
    de: "Verwaltung",
    it: "Amministrazione",
    en: "Administration",
  },
  mandate: {
    de: "Mandat",
    it: "Mandato",
    en: "Mandate",
  },
  mandates: {
    de: "Mandate",
    it: "Mandati",
    en: "Mandates",
  },
  rights: {
    de: "Rechte",
    it: "Diritti",
    en: "Rights",
  },
  users: {
    de: "Benutzer",
    it: "Utenti",
    en: "Users",
  },
  userTags: {
    de: "Nutzertags",
    it: "Tag utenti",
    en: "User tags",
  },
  outboundDocuments: {
    de: "Ausgangsdokumente",
    it: "Documenti in uscita",
    en: "Outbound documents",
  },
  inboundDocuments: {
    de: "Eingangsdokumente",
    it: "Documenti in entrata",
    en: "Inbound documents",
  },
  notifications: {
    de: "Benachrichtigungen",
    it: "Notifiche",
    en: "Notifications",
  },
  sendNewNotification: {
    de: "Neue Benachrichtigung senden",
    it: "Invia nuova notifica",
    en: "Send new notification",
  },
  newMessage: {
    de: "Neue Nachricht",
    it: "Nuovo messaggio",
    en: "New message",
  },
  signIn: {
    de: "Anmelden",
    it: "Accedi",
    en: "Sign in",
  },
  signUp: {
    de: "Registrieren",
    it: "Registrati",
    en: "Sign up",
  },
  email: {
    de: "Email",
    it: "Email",
    en: "Email",
  },
  password: {
    de: "Passwort",
    it: "Password",
    en: "Password",
  },
  backoffice: {
    de: "Backoffice",
    it: "Backoffice",
    en: "Backoffice",
  },
  name: {
    de: "Name",
    it: "Nome",
    en: "Name",
  },
  nameDe: {
    de: "Name (Deutsch)",
    it: "Nome (Tedesco)",
    en: "Name (German)",
  },
  nameIt: {
    de: "Name (Italienisch)",
    it: "Nome (Italiano)",
    en: "Name (Italian)",
  },
  nameEn: {
    de: "Name (Englisch)",
    it: "Nome (Inglese)",
    en: "Name (English)",
  },
  taxNumber: {
    de: "Steuernummer",
    it: "Codice fiscale",
    en: "Tax number",
  },
  taxNumberRubrica: {
    de: "Codice fiscale (Rubrica)",
    it: "Codice fiscale (Rubrica)",
    en: "Codice fiscale (Rubrica)",
  },
  taxNumberRubricaDesc: {
    // eslint-disable-next-line max-len
    de: "Primär-ID des Mandats. Muss mit dem AltriDa der Kundenkartei übereinstimmen - sonst wird kein Benutzer angelegt.",
    // eslint-disable-next-line max-len
    it: "ID primaria del mandato. Deve corrispondere all'AltriDa della scheda cliente - altrimenti non viene creato alcun utente.",
    // eslint-disable-next-line max-len
    en: "Primary ID of the mandate. Must match the AltriDa of the client record - otherwise no user will be created.",
  },
  status: {
    de: "Status",
    it: "Stato",
    en: "Status",
  },
  action: {
    de: "Aktion",
    it: "Azione",
    en: "Action",
  },
  userManagement: {
    de: "Verwaltung Benutzer",
    it: "Gestione utenti",
    en: "Users management",
  },
  employeeManagement: {
    de: "Verwaltung Mitarbeiter",
    it: "Gestione impiegati",
    en: "Employees management",
  },
  employee: {
    de: "Mitarbeiter",
    it: "Impiegato",
    en: "Employee",
  },
  superEmployee: {
    de: "Super-Mitarbeiter",
    it: "Super impiegato",
    en: "Super employee",
  },
  externalUserManagement: {
    de: "Verwaltung externe Benutzer",
    it: "Gestione utenti esterni",
    en: "External users management",
  },
  notificationManagement: {
    de: "Nachrichtenverwaltung",
    it: "Gestione notifiche",
    en: "Notification management",
  },
  addUser: {
    de: "Benutzer hinzufügen",
    it: "Aggiungi utente",
    en: "Add user",
  },
  addExternalUser: {
    de: "Externen Benutzer hinzufügen",
    it: "Aggiungi utente esterno",
    en: "Add external user",
  },
  mandateManagement: {
    de: "Mandatenverwaltung",
    it: "Gestione mandati",
    en: "Mandate management",
  },
  addMandate: {
    de: "Mandat hinzufügen",
    it: "Aggiungi mandato",
    en: "Add mandate",
  },
  editMandate: {
    de: "Mandat bearbeiten",
    it: "Modifica mandato",
    en: "Edit mandate",
  },
  german: {
    de: "Deutsch",
    it: "Tedesco",
    en: "German",
  },
  italian: {
    de: "Italienisch",
    it: "Italiano",
    en: "Italian",
  },
  english: {
    de: "Englisch",
    it: "Inglese",
    en: "English",
  },
  de: {
    de: "Deutsch",
    it: "Tedesco",
    en: "German",
  },
  it: {
    de: "Italienisch",
    it: "Italiano",
    en: "Italian",
  },
  en: {
    de: "Englisch",
    it: "Inglese",
    en: "English",
  },
  language: {
    de: "Sprache",
    it: "Lingua",
    en: "Language",
  },
  givenName: {
    de: "Vorname",
    it: "Nome",
    en: "Given name",
  },
  familyName: {
    de: "Nachname",
    it: "Cognome",
    en: "Family name",
  },
  phoneNumber: {
    de: "Telefonnummer",
    it: "Numero di telefono",
    en: "Phone number",
  },
  mobilePhoneNumber: {
    de: "Mobiltelefonnummer",
    it: "Numero di telefono cellulare",
    en: "Mobile phone number",
  },
  add: {
    de: "Hinzufügen",
    it: "Aggiungi",
    en: "Add",
  },
  fieldRequired: {
    de: "Dieses Feld ist erforderlich.",
    it: "Questo campo è obbligatorio.",
    en: "This field is required.",
  },
  company: {
    de: "Firma",
    it: "Azienda",
    en: "Company",
  },
  salaries: {
    de: "Löhne",
    it: "Stipendi",
    en: "Salaries",
  },
  accounting: {
    de: "Buchhaltung",
    it: "Contabilità",
    en: "Accounting",
  },
  active: {
    de: "Aktiv",
    it: "Attivo",
    en: "Active",
  },
  inactive: {
    de: "Inaktiv",
    it: "Inattivo",
    en: "Inactive",
  },
  edit: {
    de: "Bearbeiten",
    it: "Modifica",
    en: "Edit",
  },
  editUser: {
    de: "Benutzer bearbeiten",
    it: "Modifica utente",
    en: "Edit user",
  },
  editUserSuccess: {
    de: "Benutzer erfolgreich bearbeitet",
    it: "Utente modificato con successo",
    en: "User edited successfully",
  },
  editEmployeeSuccess: {
    de: "Mitarbeiter erfolgreich bearbeitet",
    it: "Impiegato modificato con successo",
    en: "Employee edited successfully",
  },
  editExternalUserSuccess: {
    de: "Externer Benutzer erfolgreich bearbeitet",
    it: "Utente esterno modificato con successo",
    en: "External user edited successfully",
  },
  addExternalUserSuccess: {
    de: "Externer Benutzer erfolgreich erstellt",
    it: "Utente esterno creato con successo",
    en: "External user created successfully",
  },
  tags: {
    de: "Tags",
    it: "Tag",
    en: "Tags",
  },
  save: {
    de: "Speichern",
    it: "Salva",
    en: "Save",
  },
  saveStar: {
    de: "Speichern*",
    it: "Salva*",
    en: "Save*",
  },
  no: {
    de: "Nein",
    it: "No",
    en: "No",
  },
  yes: {
    de: "Ja",
    it: "Sì",
    en: "Yes",
  },
  single: {
    de: "Einfach",
    it: "Singola",
    en: "Single",
  },
  double: {
    de: "Doppelt",
    it: "Doppia",
    en: "Double",
  },
  vatNumber: {
    de: "MwSt. Nr.",
    it: "P. IVA",
    en: "VAT Nr.",
  },
  editMandateSuccess: {
    de: "Mandat erfolgreich bearbeitet",
    it: "Mandato modificato con successo",
    en: "Mandate edited successfully",
  },
  createMandateSuccess: {
    de: "Mandat erfolgreich erstellt",
    it: "Mandato creato con successo",
    en: "Mandate created successfully",
  },
  externalUsers: {
    de: "Externe Benutzer",
    it: "Utenti esterni",
    en: "External users",
  },
  assignedEmployees: {
    de: "Zugewiesene PSP Mitarbeiter",
    it: "Impiegati PSP assegnati",
    en: "Assigned PSP employees",
  },
  remove: {
    de: "Entfernen",
    it: "Rimuovi",
    en: "Remove",
  },
  cancel: {
    de: "Abbrechen",
    it: "Annulla",
    en: "Cancel",
  },
  search: {
    de: "Suchen",
    it: "Cerca",
    en: "Search",
  },
  addClient: {
    de: "Kunde zum Mandat hinzufügen",
    it: "Aggiungi cliente al mandato",
    en: "Add client to mandate",
  },
  addEmployee: {
    de: "Mitarbeiter zum Mandat hinzufügen",
    it: "Aggiungi impiegato al mandato",
    en: "Add employee to mandate",
  },
  johnDoe: {
    de: "Max Mustermann",
    it: "Mario Rossi",
    en: "John Doe",
  },
  searchAUser: {
    de: "Suche einen Benutzer",
    it: "Cerca un utente",
    en: "Search for a user",
  },
  searchARight: {
    de: "Suche eine Recht",
    it: "Cerca un diritto",
    en: "Search for a right",
  },
  right1: {
    de: "Recht 1",
    it: "Diritto 1",
    en: "Right 1",
  },
  areYouSure: {
    de: "Bist du dir sicher?",
    it: "Sei sicuro?",
    en: "Are you sure?",
  },
  youAreAboutToRemove: {
    de: "Du bist dabei ",
    it: "Stai per rimuovere ",
    en: "You are about to remove ",
  },
  removeFromThisMandate: {
    de: " von diesen Mandat zu entfernen!",
    it: " da questo mandato!",
    en: " from this mandate!",
  },
  right: {
    de: "Recht",
    it: "Diritto",
    en: "Right",
  },
  canView: {
    de: "Kann einsehen",
    it: "Può visualizzare",
    en: "Can view",
  },
  loading: {
    de: "Lade...",
    it: "Caricamento...",
    en: "Loading...",
  },
  rightsManagement: {
    de: "Rechteverwaltung",
    it: "Gestione diritti",
    en: "Rights management",
  },
  addRight: {
    de: "Recht hinzufügen",
    it: "Aggiungi diritto",
    en: "Add right",
  },
  editRight: {
    de: "Recht bearbeiten",
    it: "Modifica diritto",
    en: "Edit right",
  },
  searchDocumentType: {
    de: "Suche ein Dokumenttyp",
    it: "Cerca un tipo di documento",
    en: "Search for a document type",
  },
  contactsAmount: {
    de: "Anzahl Kontakte",
    it: "Quantità contatti",
    en: "Contacts amount",
  },
  description: {
    de: "Beschreibung",
    it: "Descrizione",
    en: "Description",
  },
  descriptionDe: {
    de: "Beschreibung (Deutsch)",
    it: "Descrizione (Tedesco)",
    en: "Description (German)",
  },
  descriptionIt: {
    de: "Beschreibung (Italienisch)",
    it: "Descrizione (Italiano)",
    en: "Description (Italian)",
  },
  descriptionEn: {
    de: "Beschreibung (Englisch)",
    it: "Descrizione (Inglese)",
    en: "Description (English)",
  },
  editUserTag: {
    de: "Benutzertag bearbeiten",
    it: "Modifica tag utente",
    en: "Edit user tag",
  },
  addUserTag: {
    de: "Benutzertag hinzufügen",
    it: "Aggiungi tag utente",
    en: "Add user tag",
  },
  userTagsManagement: {
    de: "Benutzertagsverwaltung",
    it: "Gestione tag per utenti",
    en: "User tags management",
  },
  mandateTags: {
    de: "Mandatentags",
    it: "Tag per mandati",
    en: "Mandate tags",
  },
  mandateTagsManagement: {
    de: "Mandatentagsverwaltung",
    it: "Gestione tag per mandati",
    en: "Mandate tags management",
  },
  addMandateTag: {
    de: "Mandatentag hinzufügen",
    it: "Aggiungi tag per mandati",
    en: "Add mandate tag",
  },
  editMandateTag: {
    de: "Mandatentag bearbeiten",
    it: "Modifica tag per mandati",
    en: "Edit mandate tag",
  },
  settings: {
    de: "Einstellungen",
    it: "Impostazioni",
    en: "Settings",
  },
  delete: {
    de: "Löschen",
    it: "Elimina",
    en: "Delete",
  },
  deleteStar: {
    de: "Löschen*",
    it: "Elimina*",
    en: "Delete*",
  },
  confirm: {
    de: "Bestätigen",
    it: "Conferma",
    en: "Confirm",
  },
  youAreAboutToDeleteUserTag: {
    de: "Du bist dabei, diesen Benutzertag zu löschen!",
    it: "Stai per eliminare questo tag per utenti!",
    en: "You are about to remove this user tag!",
  },
  youAreAboutToRemoveNotification: {
    de: "Du bist dabei, diese Nachricht zu löschen! Bist du dir sicher?",
    it: "Stai per eliminare questa notifica! Sei sicuro?",
    en: "You are about to remove this notification! Are you sure?",
  },
  userTagDeletedSuccessfully: {
    de: "Benutzertag erfolgreich gelöscht",
    it: "Tag per utenti eliminato con successo",
    en: "User tag deleted successfully",
  },
  youCantDeleteAssignedUserTag: {
    de: "\
      *Du kannst nicht einen Benutzertag löschen, der mit mindestens einen Benutzer verknüpft ist.\
    ",
    it: "*Non puoi eliminare un tag per utenti che è assegnato ad almeno un utente.",
    en: "*You can't delete a user tag which is assigned to at least one user.",
  },
  youCantDeleteAssignedMandateTag: {
    de: "\
      *Du kannst nicht einen Mandatentag löschen, der mit mindestens ein Mandat verknüpft ist.\
    ",
    it: "*Non puoi eliminare un tag per mandati che è assegnato ad almeno un mandato.",
    en: "*You can't delete a mandate tag which is assigned to at least one mandate.",
  },
  youAreAboutToDeleteMandateTag: {
    de: "Du bist dabei, diesen Mandatentag zu löschen!",
    it: "Stai per eliminare questo tag per mandati!",
    en: "You are about to remove this mandate tag!",
  },
  mandateTagDeletedSuccessfully: {
    de: "Mandatentag erfolgreich gelöscht",
    it: "Tag per mandati eliminato con successo",
    en: "Mandate tag deleted successfully",
  },
  assignedClientEmployeeRemovedSuccessfully: {
    de: "Externer Benutzer erfolgreich vom Mandat entfernt",
    it: "Utente esterno rimosso dal mandato con successo",
    en: "External user removed from the mandate successfully",
  },
  assignedPspEmployeeRemovedSuccessfully: {
    de: "Zugewiesener PSP Mitarbeiter erfolgreich vom Mandat entfernt",
    it: "Impiegato PSP assegnato rimosso dal mandato con successo",
    en: "Assigned PSP employee removed from the mandate successfully",
  },
  assignedClientEmployeeToMandateSuccessfully: {
    de: "Externer Benutzer erfolgreich zum Mandat zugewiesen",
    it: "Utente esterno assegnato al mandato con successo",
    en: "External user assigned to the mandate successfully",
  },
  assignedPspEmployeeToMandateSuccessfully: {
    de: "PSP Mitarbeiter erfolgreich zum Mandat zugewiesen",
    it: "Impiegato PSP assegnato al mandato con successo",
    en: "PSP employee assigned to the mandate successfully",
  },
  invalidTaxNumber: {
    de: "Ungültige Steuernummer",
    it: "Codice fiscale non valido",
    en: "Invalid tax number",
  },
  invalidNumber: {
    de: "Ungültige Zahl",
    it: "Numero non valido",
    en: "Invalid number",
  },
  invalidVatNumber: {
    de: "Ungültige Mehrwertsteuernummer",
    it: "Partita IVA non valida",
    en: "Invalid VAT number",
  },
  taxNumberAlreadyInUse: {
    de: "Steuernummer bereits vergeben",
    it: "Codice fiscale già in uso",
    en: "Tax number already in use",
  },
  vatNumberAlreadyInUse: {
    de: "Mehrwertsteuernummer bereits vergeben",
    it: "Partita IVA già in uso",
    en: "VAT number already in use",
  },
  youAreAboutToDeleteRight: {
    de: "Du bist dabei, dieses Recht zu löschen!",
    it: "Stai per eliminare questo diritto!",
    en: "You are about to remove this right!",
  },
  page: {
    de: "Seite",
    it: "Pagina",
    en: "Page",
  },
  of: {
    de: "von",
    it: "di",
    en: "of",
  },
  deleteRightSuccess: {
    de: "Recht erfolgreich gelöscht",
    it: "Diritto eliminato con successo",
    en: "Right deleted successfully",
  },
  editRightSuccess: {
    de: "Recht erfolgreich bearbeitet",
    it: "Diritto modificato con successo",
    en: "Right edited successfully",
  },
  youCantDeleteAssignedRight: {
    de: "*Du kannst nicht ein Recht löschen, das mit mindestens ein Benutzer verknüpft ist.",
    it: "*Non puoi eliminare un diritto che è assegnato ad almeno un utente.",
    en: "*You can't delete a right that is assigned to at least one user",
  },
  role: {
    de: "Rolle",
    it: "Ruolo",
    en: "Role",
  },
  ACCOUNTANT: {
    de: "Buchhalter",
    it: "Contabile",
    en: "Accountant",
  },
  PAYROLL_ACCOUNTANT: {
    de: "Lohnbuchhalter",
    it: "Contabile paghe",
    en: "Payroll accountant",
  },
  payrollAccounting: {
    de: "Lohnbuchhaltung",
    it: "Contabilizzazione paghe",
    en: "Payroll accounting",
  },
  payroll_accounting: {
    de: "Lohnbuchhaltung",
    it: "Contabilizzazione paghe",
    en: "Payroll accounting",
  },
  pspEmployee: {
    de: "PSP Mitarbeiter",
    it: "Impiegato PSP",
    en: "PSP employee",
  },
  pspEmployees: {
    de: "PSP Mitarbeiter",
    it: "Impiegati PSP",
    en: "PSP employees",
  },
  selectARole: {
    de: "Wähle eine Rolle aus",
    it: "Seleziona un ruolo",
    en: "Select a role",
  },
  selectVoce: {
    de: "Voce wählen",
    it: "Seleziona voce",
    en: "Select voce",
  },
  type: {
    de: "Typ",
    it: "Tipo",
    en: "Type",
  },
  documentName: {
    de: "Dokumentenname",
    it: "Nome documento",
    en: "Document name",
  },
  date: {
    de: "Datum",
    it: "Data",
    en: "Date",
  },
  changePassword: {
    de: "Passwort bearbeiten",
    it: "Modifica password",
    en: "Edit password",
  },
  oldPassword: {
    de: "Altes Passwort",
    it: "Vecchia password",
    en: "Old password",
  },
  newPassword: {
    de: "Neues Passwort",
    it: "Nuova password",
    en: "New password",
  },
  confirmNewPassword: {
    de: "Neues Passwort bestätigen",
    it: "Conferma nuova password",
    en: "Confirm new password",
  },
  passwordChangedSuccessfully: {
    de: "Passwort erfolgreich bearbeitet",
    it: "Password modificata con successo",
    en: "Password changed successfully",
  },
  wrongPassword: {
    de: "Falsches Passwort.",
    it: "Password errata.",
    en: "Wrong password.",
  },
  passwordsDontMatch: {
    de: "Passwörter stimmen nicht überein.",
    it: "Le password non conguagliano.",
    en: "The passwords don't match.",
  },
  passwordsMatch: {
    de: "Passwörter stimmen überein",
    it: "Le password conguagliano",
    en: "The passwords match",
  },
  passwordTooShort: {
    de: "Das Passwort ist zu kurz.",
    it: "La password è troppo corta.",
    en: "The password is too short.",
  },
  passwordTooLong: {
    de: "Das Passwort ist zu lang.",
    it: "La password è troppo lunga.",
    en: "The password is too long.",
  },
  passwordMissingLetter: {
    de: "Das Passwort muss mindestens ein Buchstabe enthalten.",
    it: "La password deve contenere almeno una lettera.",
    en: "The password must contain at least one letter.",
  },
  passwordMissingUppercaseLetter: {
    de: "Das Passwort muss mindestens ein Großbuchstabe enthalten.",
    it: "La password deve contenere almeno una lettera maiuscola.",
    en: "The password must contain at least one uppercase letter.",
  },
  passwordMissingLowercaseLetter: {
    de: "Das Passwort muss mindestens ein Kleinbuchstabe enthalten.",
    it: "La password deve contenere almeno una lettera minuscola.",
    en: "The password must contain at least one lowercase letter.",
  },
  passwordMissingDigit: {
    de: "Das Passwort muss mindestens eine Ziffer enthalten.",
    it: "La password deve contenere almeno un numero.",
    en: "The password must contain at least one digit.",
  },
  passwordMissingSymbol: {
    de: "Das Passwort muss mindestens ein Symbol enthalten.",
    it: "La password deve contenere almeno un simbolo.",
    en: "The password must contain at least one symbol.",
  },
  settingsSavedSuccessfully: {
    de: "Einstellungen erfolgreich gespeichert",
    it: "Impostazioni salvate con successo",
    en: "Settings saved successfully",
  },
  app: {
    de: "App",
    it: "App",
    en: "App",
  },
  size: {
    de: "Größe",
    it: "Dimensione",
    en: "Size",
  },
  website: {
    de: "Website",
    it: "Sito web",
    en: "Website",
  },
  signOut: {
    de: "Ausloggen",
    it: "Esci",
    en: "Sign out",
  },
  createdAt: {
    de: "Erstellt am",
    it: "Creato il",
    en: "Created at",
  },
  uploadFile: {
    de: "Datei hochladen",
    it: "Carica file",
    en: "Upload file",
  },
  selectDifferentFile: {
    de: "Andere Datei auswählen",
    it: "Seleziona un altro file",
    en: "Select different file",
  },
  selectGiustificativo: {
    de: "Giustificativo wählen",
    it: "Selezione giustificativo",
    en: "Giustificativo selection",
  },
  selectDifferentFiles: {
    de: "Andere Dateien auswählen",
    it: "Seleziona altri file",
    en: "Select different files",
  },
  taxReturn: {
    de: "Erklärung",
    it: "Dichiarazione",
    en: "Tax return",
  },
  tax_return: {
    de: "Erklärung",
    it: "Dichiarazione",
    en: "Tax return",
  },
  steuererklaerung: {
    de: "Steuererklärung",
    it: "Dichiarazione dei redditi",
    en: "Tax return",
  },
  documentType: {
    de: "Dokumentart",
    it: "Tipo di documento",
    en: "Document type",
  },
  selectDocumentType: {
    de: "Dokumentart wählen",
    it: "Seleziona il tipo di documento",
    en: "Select document type",
  },
  payrollAccountingDocumentType: {
    de: "Dokumentart Lohnbuchhaltung",
    it: "Tipo di documento contabilità paghe",
    en: "Payroll accounting document type",
  },
  accountingDocumentType: {
    de: "Dokumentart Buchhaltung",
    it: "Tipo di documento contabilità",
    en: "Accounting document type",
  },
  documentTypes: {
    de: "Dokumentarten",
    it: "Tipi di documento",
    en: "Document types",
  },
  lastUploaded: {
    de: "Zuletzt hochgeladen",
    it: "Ultimi file caricati",
    en: "Last files uploaded",
  },
  uploadedFiles: {
    de: "Hochgeladene Dateien",
    it: "File caricati",
    en: "Uploaded files",
  },
  lastUpload: {
    de: "Letzter Upload",
    it: "Ultimo caricamento",
    en: "Last upload",
  },
  lastLogin: {
    de: "Letzte Anmeldung",
    it: "Ultimo accesso",
    en: "Last login",
  },
  title: {
    de: "Titel",
    it: "Titolo",
    en: "Title",
  },
  download: {
    de: "Herunterladen",
    it: "Scarica",
    en: "Download",
  },
  lastEditedAt: {
    de: "Bearbeitet am",
    it: "Modificato il",
    en: "Last edited at",
  },
  open: {
    de: "Öffnen",
    it: "Apri",
    en: "Open",
  },
  address: {
    de: "Adresse",
    it: "Indirizzo",
    en: "Address",
  },
  eventDetails: {
    de: "Eventdetails",
    it: "Dettagli eventi",
    en: "Events details",
  },
  noEventSelected: {
    // eslint-disable-next-line max-len
    de: "Kein Event ausgewählt. Um die Details eines Events einzublenden, wähle ein Event vom Kalender aus (mit Rot gekennzeichnete Daten).",
    // eslint-disable-next-line max-len
    it: "Nessun evento selezionato. Per visualizzare i dettagli di un evento, seleziona un evento dal calendario (date evidenziate in rosso).",
    // eslint-disable-next-line max-len
    en: "No event selected. To view event details, select an event from the calendar (dates highlighted red).",
  },
  noEventOn: {
    de: "Kein Event am ausgewählten Tag.",
    it: "Nessun evento nella data selezionata.",
    en: "No event on the selected date.",
  },
  aProblemOccurred: {
    de: "Ein Problem ist aufgetreten.",
    it: "Si è verificato un problema.",
    en: "A problem occurred.",
  },
  invalidCredentials: {
    de: "Ungültige Email oder ungültiges Passwort",
    it: "Email o password invalidi.",
    en: "Invalid email or password.",
  },
  gueltig: {
    de: "Gültig",
    it: "Valido",
    en: "Valid",
  },
  wechsel: {
    de: "Wechsel",
    it: "Cambio",
    en: "Switch",
  },
  neueroeffnung: {
    de: "Neueröffnung",
    it: "Nuova apertura",
    en: "New opening",
  },
  schliessung: {
    de: "Schließung",
    it: "Chiusura",
    en: "Shutdown",
  },
  uebernahme: {
    de: "Übernahme",
    it: "Subentro",
    en: "Takeover",
  },
  emailAlreadyInUse: {
    de: "Email bereits vergeben",
    it: "Email già in uso",
    en: "Email already in use",
  },
  invalidEmail: {
    de: "Ungültige Email",
    it: "Email non valida",
    en: "Invalid email",
  },
  phoneNumberAlreadyInUse: {
    de: "Telefonnummer bereits vergeben",
    it: "Numero di telefono già in uso",
    en: "Phone number already in use",
  },
  invalidMobile: {
    de: "Ungültige Mobiltelefonnummer",
    it: "Numero di telefono cellulare non valido",
    en: "Invalid mobile phone number",
  },
  mobileAlreadyInUse: {
    de: "Mobiltelefonnummer bereits vergeben",
    it: "Numero di telefono cellulare già in uso",
    en: "Mobile phone number already in use",
  },
  invalidPhoneNumber: {
    de: "Ungültige Telefonnummer",
    it: "Numero di telefono non valido",
    en: "Invalid phone number",
  },
  mandateTagEditedSuccessfully: {
    de: "Mandatentag erfolgreich bearbeitet",
    it: "Tag per mandati modificato con successo",
    en: "Mandate tag edited successfully",
  },
  userTagEditedSuccessfully: {
    de: "Benutzertag erfolgreich bearbeitet",
    it: "Tag per utenti modificato con successo",
    en: "User tag edited successfully",
  },
  newUpdateAvailable: {
    de: "Eine neue Version ist verfügbar!",
    it: "È disponibile una nuova versione!",
    en: "A new version is available!",
  },
  clickToUpdate: {
    de: "Klicke um die App zu aktualisieren und neuzustarten.",
    it: "Clicca per aggiornare l'app e ricaricarla.",
    en: "Click to update the app and restart it.",
  },
  update: {
    de: "Aktualisieren",
    it: "Aggiorna",
    en: "Update",
  },
  continue: {
    de: "Weiter",
    it: "Avanti",
    en: "Continue",
  },
  back: {
    de: "Zurück",
    it: "Indietro",
    en: "Back",
  },
  wereSigningYouIn: {
    de: "Ihr account wird gerade authentifiziert...",
    it: "Il suo account sta venendo autentificato...",
    en: "We're signing you in...",
  },
  details: {
    de: "Details",
    it: "Dettagli",
    en: "Details",
  },
  accountingAdvisor: {
    de: "Wirtschaftsprüfer und Steuerberater",
    it: "Dottore commercialista e revisore contabile",
    en: "Accounting advisor",
  },
  "n/a": {
    de: "K.A.",
    it: "N/A",
    en: "N/A",
  },
  payrollAdvisor: {
    de: "Arbeitsrechtsberater",
    it: "Consulente del lavoro",
    en: "Payroll advisor",
  },
  accountant: {
    de: "Buchhalter",
    it: "Contabile",
    en: "Accounting",
  },
  businessAccountingService: {
    de: "Dienst Buchhaltung Gewerbe",
    it: "Servizio di contabilità aziendale",
    en: "Business accounting service",
  },
  privateAccountingService: {
    de: "Dienst Buchhaltung Privat",
    it: "Servizio di contabilità privata",
    en: "Private accounting service",
  },
  privateTaxDeclarationService: {
    de: "Dienst Steuererklärung Privat",
    it: "Servizio di dichiarazione dei redditi privata",
    en: "Private tax declaration service",
  },
  microsoftLogin: {
    de: "Microsoft Login",
    it: "Login Microsoft",
    en: "Microsoft login",
  },
  youAreBeingRedirectedMicrosoftLogin: {
    // eslint-disable-next-line max-len
    de: "Du wirst gerade zur Microsoft-Seite weitergeleitet um mit deinem Microsoft-Account einzusteigen.",
    // eslint-disable-next-line max-len
    it: "Stai venendo reindirizzato alla pagina di Microsoft per effettuare l'accesso tramite il tuo account Microsoft.",
    en: "You are being redirected to the Microsoft page to login using your Microsoft account.",
  },
  ifNotRedirectedClickHere: {
    de: "Wenn du nicht automatisch weitergeiletet wirst, klicke hier",
    it: "Se non vieni reindirizzato automaticamente, clicca qui",
    en: "If you're not redirected automatically, click here",
  },
  welcomeComma: {
    de: "Willkommen,",
    it: "Benvenuto,",
    en: "Welcome,",
  },
  successfullyLoggedInMicrosoftLogin: {
    de: "Du wurdest erfolgreich mit deinem Microsoft-Account eingeloggt",
    it: "Sei stato autentificato con successo tramite il tuo account Microsoft",
    en: "You have logged in successfully via your Microsoft account",
  },
  noFiles: {
    de: "Momentan sind keine Dateien verfügbar.",
    it: "Attualmente non ci sono file disponibili.",
    en: "At the moment there are no files to be displayed.",
  },
  noPspDocuments: {
    de: "Momentan sind keine PSP-Dokumente verfügbar.",
    it: "Attualmente non ci sono documenti PSP disponibili.",
    en: "At the moment there are no PSP documents to be displayed.",
  },
  noUploads: {
    de: "Es wurden in letzter Zeit keine Dateien hochgeladen.",
    it: "Ultimamente non sono stati caricati file.",
    en: "Lately no new files were uploaded.",
  },
  resetPassword: {
    de: "Passwort zurücksetzen",
    it: "Ripristina password",
    en: "Reset password",
  },
  assignGeneratedPassword: {
    de: "Generiertes Passwort zuweisen",
    it: "Assegna password generata",
    en: "Assign generated password",
  },
  newPasswordAssignedSuccessfully: {
    de: "Neues Passwort erfolgreich zugewiesen",
    it: "Nuova password assegnata con successo",
    en: "New password assigned successfully",
  },
  failedToAssignNewPassword: {
    de: "Das Zuweisen des neuen Passworts ist fehlgeschlagen",
    it: "L'assegnazione della nuova password è fallita",
    en: "Failed to assign new password",
  },
  youAreAboutToAssignAGeneratedPassword: {
    // eslint-disable-next-line max-len
    de: "Du bist gerade dabei, diesem Benutzer ein neues, automatisch generiertes Passwort zuzuweisen. Dieses neue Passwort wird das alte überschreiben. Das neue Passwort wird dem Benutzer per E-Mail zugesendet. Bist du dir sicher?",
    // eslint-disable-next-line max-len
    it: "Stai per assegnare a questo utente una nuova password generata automaticamente. Questa nuova password sovrascriverà la precedente. La nuova password verrà inviata all’utente via e-mail. Sei sicuro?",
    // eslint-disable-next-line max-len
    en: "You are about to assign this user a new, automatically generated password. This new password will overwrite the old one. The new password will be sent to the user via email. Are you sure?",
  },
  messageSentSuccess: {
    de: "Nachricht erfolgreich abgesendet",
    it: "Messaggio inviato con successo",
    en: "Message sent successfully",
  },
  somethingWentWrong: {
    de: "Etwas ist schief gelaufen...",
    it: "Qualcosa è andato storto...",
    en: "Something went wrong...",
  },
  restoreOriginalWentWrong: {
    de: "Original wiederherstellen nicht möglich",
    it: "Impossibile ripristinare l'originale",
    en: "Unable to restore original",
  },
  titleDe: {
    de: "Überschrift (Deutsch)",
    it: "Titolo (Tedesco)",
    en: "Title (German)",
  },
  titleIt: {
    de: "Überschrift (Italienisch)",
    it: "Titolo (Italiano)",
    en: "Title (Italian)",
  },
  titleEn: {
    de: "Überschrift (Englisch)",
    it: "Titolo (Inglese)",
    en: "Title (English)",
  },
  bodyDe: {
    de: "Nachricht (Deutsch)",
    it: "Messaggio (Tedesco)",
    en: "Body (German)",
  },
  bodyIt: {
    de: "Nachricht (Italienisch)",
    it: "Messaggio (Italiano)",
    en: "Body (Italian)",
  },
  bodyEn: {
    de: "Nachricht (Englisch)",
    it: "Messaggio (Inglese)",
    en: "Body (English)",
  },
  send: {
    de: "Absenden",
    it: "Invia",
    en: "Send",
  },
  notificationEditedSuccessfully: {
    de: "Benachrichtigung erfolgreich bearbeitet.",
    it: "Notifica modificata con successo.",
    en: "Notification edited successfully.",
  },
  messageEditedSuccessfully: {
    de: "Nachricht erfolgreich bearbeitet.",
    it: "Messaggio modificato con successo.",
    en: "Message edited successfully.",
  },
  notificationDeletedSuccessfully: {
    de: "Benachrichtigung erfolgreich gelöscht.",
    it: "Notifica eliminata con successo.",
    en: "Notification deleted successfully.",
  },
  messageDeletedSuccessfully: {
    de: "Nachricht erfolgreich gelöscht.",
    it: "Messaggio eliminato con successo.",
    en: "Message deleted successfully.",
  },
  editNotification: {
    de: "Nachricht bearbeiten",
    it: "Modifica notifica",
    en: "Edit notification",
  },
  readPastTense: {
    de: "Gelesen",
    it: "Letto",
    en: "Read",
  },
  notReadPastTense: {
    de: "Nicht gelesen",
    it: "Non letto",
    en: "Not read",
  },
  read: {
    de: "Lesen",
    it: "Leggi",
    en: "Read",
  },
  recipients: {
    de: "Empfänger",
    it: "Destinatari",
    en: "Recipients",
  },
  recipientRecipients: {
    de: "Empfänger",
    it: "Destinatari(o)",
    en: "Recipient(s)",
  },
  addRecipient: {
    de: "Empfänger hinzufügen",
    it: "Aggiungi destinatario",
    en: "Add recipient",
  },
  sendNotificationWithoutRecipientsWarning: {
    de: "Du bist dabei, eine Nachricht ohne Empfänger abzusenden. Bist du dir sicher?",
    it: "Stai per inviare una notifica senza destinatari. Sei sicuro?",
    en: "You are about to send a notification without recipients. Are you sure?",
  },
  createTypeWithoutDocumentTypeOrFolderWarning: {
    // eslint-disable-next-line max-len
    de: "Du bist dabei, ein Recht zu erstellen, ohne diesem ein Dokumenttyp oder ein Ordner zuzuweisen. Bist du dir sicher?",
    // eslint-disable-next-line max-len
    it: "Stai per creare un diritto senza associarvi alcun tipo di documento o cartella. Sei sicuro?",
    // eslint-disable-next-line max-len
    en: "You are about to create a right without associating any document type or folder with it. Are you sure?",
  },
  noDocumentTypeOrFolderAfterRightEditWarning: {
    // eslint-disable-next-line max-len
    de: "Nach dieser Änderung, wird dieses Recht kein Dokumenttyp oder Ordner zugewiesen haben. Bist du dir sicher?",
    // eslint-disable-next-line max-len
    it: "Dopo questa modifica, questo diritto non sarebbe assegnato ad alcun tipo di documento o cartella. Sei sicuro?",
    // eslint-disable-next-line max-len
    en: "After this change, this right will not be assigned to any document type or folder. Are you sure?",
  },
  signInWithThisMandate: {
    de: "Mit diesem Mandat einsteigen",
    it: "Accedi con questo Mandato",
    en: "Sign in with this mandate",
  },
  pspDocuments: {
    de: "PSP Dokumente",
    it: "Documenti PSP",
    en: "PSP documents",
  },
  loehne: {
    de: "Löhne",
    it: "Paghe",
    en: "Payroll",
  },
  registro_fatture_di_aquisto: {
    de: "Eingangsrechnungsbuch",
    it: "Registro fatture di acquisto",
    en: "Purchase invoice register",
  },
  registro_fatture_di_vendita: {
    de: "Ausgangsrechnungsbuch",
    it: "Registro fatture di vendita",
    en: "Sales invoice register",
  },
  registro_iva_dei_corrispettivi: {
    de: "MwSt. Tageseinnahmen",
    it: "Incassi giornalieri IVA",
    en: "Daily VAT receipts",
  },
  registro_iva_riepilogativo: {
    de: "MwSt. Jahresabrechnung (jährlich)",
    it: "Dichiarazione annuale IVA (annuale)",
    en: "Annual VAT return (yearly)",
  },
  mden_annuale: {
    de: "MwSt. Jahresabrechnung (jährlich)",
    it: "Rendiconto annuale IVA (annuale)",
    en: "Annual VAT statement (annual)",
  },
  giornale_contabile: {
    de: "Journal",
    it: "Giornale contabile",
    en: "General journal",
  },
  partitario: {
    de: "Kontenblatt",
    it: "Partitario",
    en: "Account ledger",
  },
  mod_740: {
    de: "Formular 740",
    it: "Modello 740",
    en: "Form 740",
  },
  mod_730: {
    de: "Formular 730",
    it: "Modello 730",
    en: "Form 730",
  },
  mod_750: {
    de: "Formular 750",
    it: "Modello 750",
    en: "Form 750",
  },
  mod_760: {
    de: "Formular 760",
    it: "Modello 760",
    en: "Form 760",
  },
  mod_770: {
    de: "Erklärung 770",
    it: "Dichiarazione 770",
    en: "770 declaration",
  },
  f24: {
    de: "F24",
    it: "F24",
    en: "F24",
  },
  abschreiberegister: {
    de: "Abschreiberegister",
    it: "Registro ammortamenti",
    en: "Depreciation register",
  },
  dired_steuererklaerung: {
    de: "Direkte Steuererklärung",
    it: "Dichiarazione imposte dirette",
    en: "Direct taxes return",
  },
  eu_bilanz: {
    de: "EU-Bilanz",
    it: "Bilancio UE",
    en: "EU balance sheet",
  },
  intento: {
    de: "Absichtserklärungen",
    it: "Dichiarazioni d’intento",
    en: "Declarations of intent",
  },
  intra: {
    de: "Intrastat-Meldungen",
    it: "Dichiarazioni Intrastat",
    en: "Intrastat returns",
  },
  iva_annual: {
    de: "MwSt. Jahreserklärung",
    it: "Dichiarazione IVA annuale",
    en: "Annual VAT return",
  },
  liquidazione_periodica: {
    de: "USt.-Voranmeldung",
    it: "Liquidazione periodica IVA",
    en: "Periodic VAT return",
  },
  msit_bilanz: {
    de: "Bilanz",
    it: "Bilancio",
    en: "Balance sheet",
  },
  msit_kunden: {
    de: "Kundenforderungen",
    it: "Crediti clienti",
    en: "Client receivables",
  },
  msit_lieferanten: {
    de: "Lieferantenverbindlichkeiten",
    it: "Debiti fornitori",
    en: "Supplier payables",
  },
  msit_vergleichsbilanz: {
    de: "Vergleichsbilanz",
    it: "Bilancio comparativo",
    en: "Comparative balance sheet",
  },
  op_liste: {
    de: "Offene Posten",
    it: "Partite aperte",
    en: "Open items",
  },
  op_liste_kunden: {
    de: "Offene Posten - Kunden",
    it: "Partite aperte - Clienti",
    en: "Open items - Clients",
  },
  op_liste_lieferanten: {
    de: "Offene Posten – Lieferanten",
    it: "Partite aperte - Fornitori",
    en: "Open items - Suppliers",
  },
  unica: {
    de: "Unico – Steuererklärung",
    it: "Modello Unico",
    en: "Unico tax return",
  },
  registro_iva_acquisti: {
    de: "USt.-Eingangsregister",
    it: "Registro IVA acquisti",
    en: "VAT purchases register",
  },
  submittedDocuments: {
    de: "Eingereichte Unterlagen",
    it: "Documenti inviati",
    en: "Submitted documents",
  },
  noNotifications: {
    de: "Momentan sind keine Nachrichten verfügbar.",
    it: "Attualmente non ci sono alcune notifiche disponibili.",
    en: "There are currently no notifications available.",
  },
  noCircularLetters: {
    de: "Momentan sind keine Rundschreiben verfügbar.",
    it: "Momentaneamente non ci sono alcune comunicazioni circolari disponibili.",
    en: "There are currently no circular letters available.",
  },
  filters: {
    de: "Filter",
    it: "Filtri",
    en: "Filters",
  },
  abgelehnt: {
    de: "Abgelehnt",
    it: "Rifiutato",
    en: "Rejected",
  },
  abgeschlossen: {
    de: "Abgeschlossen",
    it: "Completato",
    en: "Completed",
  },
  couldntLoadMandateCount: {
    de: "Anzahl an Mandate konnte nicht geladen werden.",
    it: "Non è stato possibile caricare la quantità totale di mandati.",
    en: "Couldn't load total number of mandates.",
  },
  couldntLoadMandate: {
    de: "Mandat konnte nicht geladen werden.",
    it: "Non è stato possibile caricare il mandato.",
    en: "Couldn't load mandate.",
  },
  couldntLoadMandateStructure: {
    de: "Mandatstruktur konnte nicht geladen werden.",
    it: "Non è stato possibile caricare la struttura del mandato.",
    en: "Couldn't load mandate structure.",
  },
  couldntLoadExternalUsers: {
    de: "Externe Benutzer konnten nicht geladen werden.",
    it: "Non è stato possibile caricare gli utenti esterni.",
    en: "Couldn't load external users.",
  },
  couldntLoadUsers: {
    de: "Benutzer konnten nicht geladen werden.",
    it: "Non è stato possibile caricare gli utenti.",
    en: "Couldn't load users.",
  },
  couldntLoadRights: {
    de: "Rechte konnten nicht geladen werden.",
    it: "Non è stato possibile caricare i diritti.",
    en: "Couldn't load rights.",
  },
  couldntLoadRight: {
    de: "Recht konnte nicht geladen werden.",
    it: "Non è stato possibile caricare il diritto.",
    en: "Couldn't load right.",
  },
  couldntLoadRightDeletability: {
    de: "Konnte nicht kontrollieren, ob das Recht löschbar ist oder nicht.",
    it: "Non è stato possibile controllare se il diritto è cancellabile o meno.",
    en: "Couldn't check whether the right is deletable or not.",
  },
  couldntLoadRightDocumentTypes: {
    de: "Die Dokumenttypen des Rechts konnten nicht geladen werden.",
    it: "Non è stato possibile caricare i tipi di documenti.",
    en: "Couldn't load document types.",
  },
  couldntLoadUserTags: {
    de: "Nutzertags konnten nicht geladen werden.",
    it: "Non è stato possibile caricare i tag degli utenti.",
    en: "Couldn't load user tags.",
  },
  couldntLoadUserTag: {
    de: "Nutzertag konnte nicht geladen werden.",
    it: "Non è stato possibile caricare il tag dell'utente.",
    en: "Couldn't load user tag.",
  },
  couldntLoadMandateTags: {
    de: "Mandatentags konnten nicht geladen werden.",
    it: "Non è stato possibile caricare i tag dei mandati.",
    en: "Couldn't load mandate tags.",
  },
  couldntLoadMandateTag: {
    de: "Mandatentag konnte nicht geladen werden.",
    it: "Non è stato possibile caricare il tag del mandato.",
    en: "Couldn't load mandate tag.",
  },
  couldntLoadInboundDocuments: {
    de: "Eingangsdokumente konnten nicht geladen werden.",
    it: "Non è stato possibile caricare i documenti in entrata.",
    en: "Couldn't load inbound documents.",
  },
  couldntLoadInboundDocumentCount: {
    de: "Anzahl an Eingangsdokumente konnte nicht geladen werden.",
    it: "Non è stato possibile caricare la quantità totale di documenti in entrata.",
    en: "Couldn't load total number of inbound documents.",
  },
  couldntLoadOutboundDocuments: {
    de: "Ausgangsdokumente konnten nicht geladen werden.",
    it: "Non è stato possibile caricare i documenti in uscita.",
    en: "Couldn't load outbound documents.",
  },
  couldntLoadOutboundDocumentCount: {
    de: "Anzahl an Ausgangsdokumente konnte nicht geladen werden.",
    it: "Non è stato possibile caricare la quantità totale di documenti in uscita.",
    en: "Couldn't load total number of outbound documents.",
  },
  couldntLoadNotifications: {
    de: "Benachrichtigungen konnten nicht geladen werden.",
    it: "Non è stato possibile caricare le notifiche.",
    en: "Couldn't load notifications.",
  },
  couldntLoadMessages: {
    de: "Nachrichten konnten nicht geladen werden.",
    it: "Non è stato possibile caricare i messaggi.",
    en: "Couldn't load messages.",
  },
  couldntLoadNotificationCount: {
    de: "Anzahl an Benachrichtigungen konnte nicht geladen werden.",
    it: "Non è stato possibile caricare la quantità totale di notifiche.",
    en: "Couldn't load total number of notifications.",
  },
  couldntLoadMessageCount: {
    de: "Anzahl an Nachrichten konnte nicht geladen werden.",
    it: "Non è stato possibile caricare la quantità totale di messaggi.",
    en: "Couldn't load total number of messages.",
  },
  couldntLoadNotification: {
    de: "Benachrichtigung konnte nicht geladen werden.",
    it: "Non è stato possibile caricare la notifica.",
    en: "Couldn't load notification.",
  },
  couldntLoadMessage: {
    de: "Nachricht konnte nicht geladen werden.",
    it: "Non è stato possibile caricare il messaggio.",
    en: "Couldn't load message.",
  },
  couldntLoadUploadedDocuments: {
    de: "Hochgeladene Dokumente konnten nicht geladen werden.",
    it: "Non è stato possibile caricare i documenti caricati.",
    en: "Couldn't load uploaded documents.",
  },
  couldntLoadPspDocuments: {
    de: "PSP-Dokumente konnten nicht geladen werden.",
    it: "Non è stato possibile caricare i documenti PSP.",
    en: "Couldn't load PSP documents.",
  },
  couldntLoadPspDocumentCount: {
    de: "Anzahl an PSP-Dokumente konnte nicht geladen werden.",
    it: "Non è stato possibile caricare la quantità totale di documenti PSP.",
    en: "Couldn't load total number of PSP documents.",
  },
  couldntLoadCircularLetters: {
    de: "Rundschreiben konnten nicht geladen werden.",
    it: "Non è stato possibile caricare le comunicazioni circolari.",
    en: "Couldn't load circular letters.",
  },
  couldntLoadCalendarEntries: {
    de: "Kalendereinträge konnten nicht geladen werden.",
    it: "Non è stato possibile caricare le voci del calendario.",
    en: "Couldn't load calendar entries.",
  },
  noMandateSelected: {
    de: "Es wurde kein Mandat ausgewählt.",
    it: "Non è stato selezionato alcun mandato.",
    en: "No mandate has been selected.",
  },
  noContactAvailable: {
    // eslint-disable-next-line max-len
    de: "Momentan ist kein Kontakt verfügbar, weil Ihnen noch weder ein Berater noch eine Filiale zugeordnet worden ist.",
    // eslint-disable-next-line max-len
    it: "Momentaneamente non sono disponibili contatti, perché non le è ancora stato assegnato né un consulente, né una filiale.",
    // eslint-disable-next-line max-len
    en: "Momentarily there are no contacts available, because no consultant or location has been assigned to You yet.",
  },
  createRightSuccess: {
    de: "Recht erfolgreich erstellt",
    it: "Diritto creato con successo",
    en: "Right created successfully",
  },
  createUserTagSuccess: {
    de: "Benutzertag erfolgreich erstellt",
    it: "Tag per utenti creato con successo",
    en: "User tag created successfully",
  },
  sendCode: {
    de: "Code senden",
    it: "Invia codice",
    en: "Send code",
  },
  code: {
    de: "Code",
    it: "Codice",
    en: "Code",
  },
  passwordResetCodePlaceholder: {
    de: "XXX-XXX",
    it: "XXX-XXX",
    en: "XXX-XXX",
  },
  codeWasSentTo: {
    de: "Der Verifizierungscode wurde an {email} zugesendet.",
    it: "Il codice di verifica è stato inviato a {email}.",
    en: "The verification code was sent to {email}.",
  },
  verify: {
    de: "Verifizieren",
    it: "Verifica",
    en: "Verify",
  },
  passwordCannotBeBlank: {
    de: "Das Passwort kann nicht leer sein oder nur Leerzeichen enthalten.",
    it: "La password non può essere vuota o contenere solo spazi.",
    en: "The password cannot be empty or contain only spaces.",
  },
  passwordNeedsToBeMinimumLength: {
    de: "Das Passwort muss mindestens 7 Zeichen lang sein.",
    it: "La password deve essere lunga almeno 7 caratteri.",
    en: "The password needs to have a minimum length of 7 characters.",
  },
  administrator: {
    de: "Administrator",
    it: "Amministratore",
    en: "Administrator",
  },
  couldntLoadMandates: {
    de: "Mandate konnten nicht geladen werden.",
    it: "Non è stato possibile caricare i mandati.",
    en: "Couldn't load mandates.",
  },
  couldntFindNotification: {
    de: "Benachrichtigung konnte nicht gefunden werden.",
    it: "Non è stato possibile trovare la notifica.",
    en: "Couldn't find notification.",
  },
  couldntFindMessage: {
    de: "Nachricht konnte nicht gefunden werden.",
    it: "Non è stato possibile trovare il messaggio.",
    en: "Couldn't find message.",
  },
  createMandateTagSuccess: {
    de: "Mandatentag erfolgreich erstellt.",
    it: "Tag per mandati creato con successo.",
    en: "Mandate tag created successfully.",
  },
  autocompleteItalianAndEnglish: {
    de: "Italienisch und Englisch automatisch ausfüllen",
    it: "Compila italiano e inglese automaticamente",
    en: "Autocomplete italian and english",
  },
  execute: {
    de: "Ausführen",
    it: "Esegui",
    en: "Execute",
  },
  actionExecutedSuccessfully: {
    de: "Aktion erfolgreich ausgeführt",
    it: "Azione eseguita con successo",
    en: "Action executed successfully",
  },
  couldntDeleteOutboundDocument: {
    de: "Das Ausgangsdokument konnte nicht gelöscht werden.",
    it: "Non è stato possibile eliminare il documento in uscita.",
    en: "The outbound document couldn't be deleted.",
  },
  cantDeleteDownloadedOutboundDocument: {
    // eslint-disable-next-line max-len
    de: "Du kannst kein Ausgangsdokument löschen, das bereits von mindestens einem Nutzer heruntergeladen wurde.",
    it: "Non puoi eliminare un documento in uscita già scaricato da almeno un utente.",
    // eslint-disable-next-line max-len
    en: "You can’t delete an outbound document that has already been downloaded by at least one user.",
  },
  youAreAboutToDeleteOutboundDocument: {
    // eslint-disable-next-line max-len
    de: "Du bist dabei, dieses Ausgangsdokument zu löschen. Dieser Vorgang kann nicht rückgängig gemacht werden. Bist du dir sicher?",
    // eslint-disable-next-line max-len
    it: "Stai per eliminare questo documento in uscita. Questa azione non può essere annullata. Sei sicuro di voler procedere?",
    en: "You’re about to delete this outbound document. This action can’t be undone. Are you sure?",
  },
  cookieDialogTitle: {
    de: "Cookies und lokaler Speicher",
    it: "Cookie e archiviazione locale",
    en: "Cookies & local storage",
  },
  cookieDialogBody: {
    // eslint-disable-next-line max-len
    de: "Wir verwenden essenzielle Cookies und Speicher, um Sie anzumelden, die App zu schützen und Kernfunktionen bereitzustellen. Mit Ihrer Zustimmung speichern wir außerdem das ausgewählte Mandat auf diesem Gerät, damit es beim nächsten Mal vorausgewählt ist. Dies ist optional und wird nicht für Werbung genutzt.",
    // eslint-disable-next-line max-len
    it: "Utilizziamo cookie e archiviazione essenziali per autenticarti, mantenere sicura l’app e abilitare le funzionalità principali. Con il tuo consenso, salviamo anche il mandato selezionato su questo dispositivo per pre-selezionarlo al prossimo accesso. Questa funzione è facoltativa e non viene utilizzata per pubblicità.",
    // eslint-disable-next-line max-len
    en: "We use essential cookies and storage to sign you in, keep the app secure, and enable core features. With your permission, we also save your selected mandate on this device so it’s pre-selected next time. This is optional and not used for advertising.",
  },
  cookieDialogLink: {
    de: "Cookie- und Speicherrichtlinie",
    it: "Informativa su cookie e archiviazione",
    en: "Cookie & Storage Policy",
  },
  noMandatesFoundWithCurrentSearchQuery: {
    de: "Es wurde kein Mandat mit dieser Suchanfrage gefunden.",
    it: "Non sono stati trovati mandati con la ricerca corrente.",
    en: "No Mandates found with the current search query.",
  },
  notAssignedToAnyMandateAsEmployee: {
    de: "Du wurdest noch keinem Mandat als Mitarbeiter zugeordnet.",
    it: "Non sei ancora stato assegnato ad alcun mandato come impiegato.",
    en: "You haven't been assigned to any mandate as an employee yet.",
  },
  consentAllCookies: {
    de: "Alle Cookies zulassen",
    it: "Consenti tutti i cookie",
    en: "Consent all cookies",
  },
  consentEssentialCookiesOnly: {
    de: "Nur essentielle Cookies zulassen",
    it: "Consenti solo cookie essenziali",
    en: "Consent essential cookies only",
  },
  cookiePreferences: {
    de: "Cookie-Präferenzen",
    it: "Preferenze sui cookie",
    en: "Cookie preferences",
  },
  allCookies: {
    de: "Alle Cookies",
    it: "Tutti i cookie",
    en: "All cookies",
  },
  functionalCookiesOnly: {
    de: "Nur funktionale Cookies",
    it: "Solo cookie funzionali",
    en: "Only functional cookies",
  },
  cookiesPageTitle: {
    de: "Cookie- und Speicherrichtlinie",
    it: "Informativa su Cookie e Archiviazione",
    en: "Cookie & Storage Policy",
  },
  privacyPageTitle: {
    de: "Datenschutzerklärung",
    it: "Informativa sulla Privacy ed il trattamento dei dati personali",
    en: "Privacy policy and handling of personal data",
  },
  cookiesPagePurposeTitle: {
    de: "Zweck",
    it: "Finalità",
    en: "Purpose",
  },
  cookiesPagePurpose: {
    // eslint-disable-next-line max-len
    de: "Diese Seite erläutert unseren Einsatz von Cookies und ähnlichen Technologien (z.B. localStorage, sessionStorage, Cache Storage/Service Worker) auf Ihrem Gerät. Sie können Ihre Auswahl jederzeit über den Cookie-Präferenzen-Teil der Einstellungen-Seite in der App ändern.",
    // eslint-disable-next-line max-len
    it: "Questa pagina descrive l’uso di cookie e tecnologie simili (localStorage, sessionStorage, Cache Storage/service worker) sul tuo dispositivo. Puoi modificare le scelte in qualsiasi momento tramite la sezione riguardante le preferenze sui cookie nelle impostazioni dell’app.",
    // eslint-disable-next-line max-len
    en: "This page explains how we use cookies and similar technologies (such as localStorage, sessionStorage, and Cache Storage/service workers) on your device. You can change your choices at any time via the cookie preferences section in the Settings page of the app.",
  },
  cookiesPageControllerTitle: {
    de: "Verantwortlicher",
    it: "Titolare",
    en: "Controller",
  },
  cookiesPageController: {
    de: "PSP STP srl - Dr.-Streiter Str. 32, 39100 Bozen, Italien - Kontakt: info@psp-bz.it",
    it: "PSP STP srl - Via Dr.-Streiter 32, 39100 Bolzano, Italia - Contatto: info@psp-bz.it",
    en: "PSP STP srl - Via Dr.-Streiter 32, 39100 Bolzano, Italy - Contact: info@psp-bz.it",
  },
  cookiesPageTechnologiesTitle: {
    de: "Abgedeckte Technologien",
    it: "Tecnologie coperte",
    en: "Technologies covered",
  },
  cookiesPageTechnologies: {
    // eslint-disable-next-line max-len
    de: "HTTP-Cookies, localStorage, sessionStorage, IndexedDB, Cache Storage (Service Worker) und ähnliche Gerätespeicher.",
    // eslint-disable-next-line max-len
    it: "Cookie HTTP, localStorage, sessionStorage, IndexedDB, Cache Storage (service worker) e archiviazioni simili.",
    // eslint-disable-next-line max-len
    en: "HTTP cookies, localStorage, sessionStorage, IndexedDB, Cache Storage (service worker) and similar device storage.",
  },
  cookiesPageCategoriesTitle: {
    de: "Kategorien & Rechtsgrundlagen",
    it: "Categorie e basi giuridiche",
    en: "Categories & legal basis",
  },
  cookiesPageCategories: {
    // eslint-disable-next-line max-len
    de: "Essentiell (ohne Opt-in): Erforderlich für Anmeldung, Sicherheit und Kernfunktionen. Rechtsgrundlage: ePrivacy-Ausnahme „unbedingt erforderlich“; DSGVO Art. 6(1)(b) Vertragserfüllung und/oder Art. 6(1)(f) berechtigte Interessen (Sicherheit).\nFunktional (Einwilligung): Optionale Verbesserungen, nur bei Zustimmung. Rechtsgrundlage: DSGVO Art. 6(1)(a) Einwilligung (jederzeit widerrufbar).",
    // eslint-disable-next-line max-len, quotes
    it: 'Essenziali (senza opt-in): Necessari per autenticarti, garantire la sicurezza e fornire le funzionalità principali. Base giuridica: eccezione ePrivacy "strettamente necessario"; GDPR art. 6(1)(b) necessità contrattuale e/o art. 6(1)(f) legittimo interesse (sicurezza).\nFunzionali(consenso): Miglioramenti opzionali utilizzati solo con il tuo consenso. Base giuridica: GDPR art. 6(1)(a) consenso (revocabile in qualsiasi momento).',
    // eslint-disable-next-line max-len, quotes
    en: 'Essential (no opt-in): Required to sign you in, keep the app secure, and provide core features. Legal basis: ePrivacy "strictly necessary"; GDPR Art. 6(1)(b) contract necessity and/or Art. 6(1)(f) legitimate interests (security).\nFunctional (consent): Optional improvements, used only if you agree. Legal basis: GDPR Art. 6(1)(a) consent (you can withdraw anytime).',
  },
  cookiesPageChoicesTitle: {
    de: "Ihre Wahlmöglichkeiten",
    it: "Le tue scelte",
    en: "Your choices",
  },
  cookiesPageChoices: {
    // eslint-disable-next-line max-len
    de: "Über der Cookie-Präferenzen-Einstellung in den Einstellungen der App können Sie funktionale Speicherung annehmen oder ablehnen. Sie können die Einwilligung jederzeit widerrufen. Sie können den Speicher auch in den Browser-Einstellungen zurücksetzen.",
    // eslint-disable-next-line max-len
    it: "Usa la sezione riguardante le preferenze sui cookie nella pagina Impostazioni dell'app per accettare o rifiutare l'archiviazione Funzionale. Puoi revocare il consenso in qualsiasi momento. Puoi anche cancellare i dati tramite le impostazioni del tuo browser.",
    // eslint-disable-next-line max-len
    en: "Use the cookie preferences section in the Settings page of the app to accept or refuse Functional storage. You can withdraw consent at any time. You can also clear storage via your browser settings.",
  },
  cookiesPageThirdPartiesTitle: {
    de: "Dritte",
    it: "Terze parti",
    en: "Third parties",
  },
  cookiesPageThirdParties: {
    // eslint-disable-next-line max-len
    de: "Während der Anmeldung kann Microsoft (Microsoft Entra / Microsoft identity platform) auf seinen Domains Cookies setzen, um eine sichere Anmeldung abzuschließen und Betrug zu verhindern.",
    // eslint-disable-next-line max-len
    it: "Durante l'autenticazione, Microsoft (Microsoft Entra / Microsoft identity platform) può impostare cookie sui propri domini per completare l'accesso sicuro e prevenire frodi.",
    // eslint-disable-next-line max-len
    en: "During authentication, Microsoft (Microsoft Entra / Microsoft identity platform) may set cookies on its domains to complete secure sign-in and prevent fraud.",
  },
  cookiesPageItemsTitle: {
    de: "Geräteseitige Einträge, die wir nutzen",
    it: "Voci di archiviazione sul dispositivo",
    en: "Device-side items we use",
  },
  cookiesPageContactTitle: {
    de: "Kontakt zu Cookies",
    it: "Contatti per i cookie",
    en: "How to contact us about cookies",
  },
  cookiesPageContact: {
    de: "Für Fragen zu dieser Richtlinie oder Ihren Optionen: info@psp-bz.it",
    it: "Per domande su questa informativa o sulle tue scelte: info@psp-bz.it",
    en: "For questions about this policy or your choices: info@psp-bz.it",
  },
  cookiesLastUpdate: {
    de: "Zuletzt akutalisiert: 13.10.2025",
    it: "Ultimo aggiornamento: 13/10/2025",
    en: "Last update: 10/13/2025",
  },
  privacyLastUpdate: {
    de: "Zuletzt aktualisiert: 24.10.2025",
    it: "Ultimo aggiornamento: 24/10/2025",
    en: "Last updated: 10/24/2025",
  },
  privacyWhoWeAreTitle: {
    de: "1) Wer wir sind",
    it: "1) Chi siamo",
    en: "1) Who we are",
  },
  privacyWhoWeAreController: {
    de: "Verantwortlicher (Controller): PSP STP srl - Via Dr.-Streiter 32, 39100 Bozen, Italien",
    it: "Titolare del trattamento: PSP STP srl - Via Dr.-Streiter 32, 39100 Bolzano, Italia",
    en: "Controller: PSP STP srl - Via Dr.-Streiter 32, 39100 Bolzano, Italy",
  },
  privacyWhoWeAreContact: {
    de: "Kontakt: info@psp-bz.it",
    it: "Contatto: info@psp-bz.it",
    en: "Contact: info@psp-bz.it",
  },
  privacyWhoWeAreDpo: {
    de: "Datenschutzbeauftragter (DPO): Florian Brunner",
    it: "Responsabile della Protezione dei Dati (RPD/DPO): Florian Brunner",
    en: "Data Protection Officer (DPO): Florian Brunner",
  },
  privacyWhatThisPolicyCoversTitle: {
    de: "2) Was diese Erklärung abdeckt",
    it: "2) Cosa copre questa informativa",
    en: "2) What this policy covers",
  },
  privacyWhatThisPolicyCoversBody: {
    // eslint-disable-next-line max-len
    de: "Diese Information beschreibt, wie wir personenbezogene Daten in unserer Webanwendung verarbeiten, die von Mandanten von PSP STP srl zur Dokumentenübermittlung, Kontoverwaltung und zum Empfang von Mitteilungen genutzt wird. Wir stellen diese Informationen in klarer, verständlicher Sprache gemäß Art. 12 DSGVO bereit.",
    // eslint-disable-next-line max-len
    it: "La presente informativa descrive come trattiamo i dati personali nella nostra applicazione web utilizzata dai clienti di PSP STP srl per scambiare documenti, gestire i propri account e ricevere comunicazioni. Forniamo queste informazioni in modo chiaro e comprensibile, come richiesto dall’art. 12 GDPR.",
    // eslint-disable-next-line max-len
    en: "This notice explains how we process personal data in our web application used by clients of PSP STP srl to exchange documents, manage their accounts, and receive communications. We provide this information in clear, concise language as required by GDPR Art. 12.",
  },
  privacyWhatDataWeProcessTitle: {
    de: "3) Welche Daten wir verarbeiten",
    it: "3) Quali dati trattiamo",
    en: "3) What data we process",
  },
  privacyWhatDataWeProcessAccountData: {
    de: "Kontodaten (Mandanten): Name, E-Mail, Passwort-Hash, Konto-IDs, Einstellungen.",
    // eslint-disable-next-line max-len
    it: "Dati dell’account (clienti): nome, e-mail, hash della password, ID dell’account, impostazioni.",
    en: "Account data (clients): name, email, password hash, account IDs, settings.",
  },
  privacyWhatDataWeProcessBusinessData: {
    // eslint-disable-next-line max-len
    de: "Daten aus der Geschäftsbeziehung: Details zu Steuer-/Beratungsmandaten, Dateimetadaten, Support-Interaktionen.",
    // eslint-disable-next-line max-len
    it: "Dati relativi al rapporto professionale: dettagli dell’incarico fiscale/di consulenza, metadati dei file, interazioni con il supporto.",
    en: "Business relationship data: tax-engagement details, file metadata, support interactions.",
  },
  privacyWhatDataWeProcessUploadedContent: {
    // eslint-disable-next-line max-len
    de: "Hochgeladene Inhalte: Dokumente, die Mandanten hochladen (können personenbezogene Daten des Mandanten oder Dritter enthalten).",
    // eslint-disable-next-line max-len
    it: "Contenuti caricati: documenti caricati dai clienti (che possono includere dati personali del cliente o di terzi).",
    // eslint-disable-next-line max-len
    en: "Uploaded content: documents clients upload (which may include personal data of the client or third parties).",
  },
  privacyWhatDataWeProcessCommunicationData: {
    // eslint-disable-next-line max-len
    de: "Kommunikationsdaten: In-App-Benachrichtigungen, E-Mail-Zustell-/Öffnungsereignisse, Nachrichteninhalte über die Plattform.",
    // eslint-disable-next-line max-len
    it: "Dati di comunicazione: notifiche in-app, eventi di consegna/apertura e-mail, contenuti dei messaggi inviati tramite la piattaforma.",
    // eslint-disable-next-line max-len
    en: "Communication data: in-app notifications, email delivery/open events, message content sent via the platform.",
  },
  privacyWhatDataWeProcessLogs: {
    de: "Log-/Technische Daten: Gerät, IP-Adresse, Zeitstempel, Sicherheitsprotokolle.",
    it: "Dati tecnici/log: dispositivo, indirizzo IP, marca temporale, log di sicurezza.",
    en: "Log/technical data: device, IP address, timestamps, security logs.",
  },
  privacyWhatDataWeProcessImportedData: {
    // eslint-disable-next-line max-len
    de: "Importierte Daten: Einige Mandantenkonten und Kontaktdaten wurden aus internen Systemen der PSP STP srl importiert (nicht direkt von Ihnen erhoben).",
    // eslint-disable-next-line max-len
    it: "Dati importati: alcuni account e recapiti dei clienti sono stati importati dai sistemi interni di PSP STP srl (non raccolti direttamente dall’interessato).",
    // eslint-disable-next-line max-len
    en: "Imported data: some client accounts and contact details were imported from PSP STP srl’s internal systems (not collected directly from you).",
  },
  privacyWhatDataWeProcessImportedDataCategoriesTitle: {
    de: "{{KATEGORIEN UND QUELLEN}}",
    it: "{{CATEGORIE E FONTI}}",
    en: "{{CATEGORIES AND SOURCES}}",
  },
  privacyWhereWeGotYourDataTitle: {
    de: "4) Woher wir Ihre Daten haben",
    it: "4) Origine dei dati",
    en: "4) Where we got your data",
  },
  privacyWhereWeGotYourDataDirectly: {
    // eslint-disable-next-line max-len
    de: "Direkt von Ihnen (z. B. beim Hochladen von Dokumenten, Kontakt zum Support, Ändern von Kontoeinstellungen).",
    // eslint-disable-next-line max-len
    it: "Direttamente da te (ad es. quando carichi documenti, contatti il supporto, modifichi le impostazioni dell’account).",
    en: "Directly from you (when you upload documents, contact support, edit account settings).",
  },
  privacyWhereWeGotYourDataIndirectly: {
    // eslint-disable-next-line max-len
    de: "Indirekt von PSP STP srl (importierte Mandanten- und Mandatslisten zur Einrichtung Ihres Kontos und zur Leistungserbringung).",
    // eslint-disable-next-line max-len
    it: "Indirettamente da PSP STP srl (elenchi clienti e mandati importati per creare il tuo account e fornire i servizi).",
    // eslint-disable-next-line max-len
    en: "Indirectly from PSP STP srl (client and mandate lists imported to set up your account and enable services).",
  },
  privacyWhereWeGotYourDataSeeSection13: {
    de: "Siehe Abschnitt 13 zur Benachrichtigungsfrist (Art. 14 DSGVO).",
    it: "Vedi la Sezione 13 sui tempi di informativa (art. 14 GDPR).",
    en: "See Section 13 on when we notify you (GDPR Art. 14).",
  },
  privacyWhyWeProcessDataTitle: {
    de: "5) Zwecke der Verarbeitung & Rechtsgrundlagen",
    it: "5) Finalità del trattamento e basi giuridiche",
    en: "5) Why we process data (purposes) & legal bases",
  },
  privacyWhyWeProcessDataHeader: {
    de: "Wir verarbeiten personenbezogene Daten nur bei Vorliegen einer Rechtsgrundlage:",
    it: "Trattiamo i dati personali solo in presenza di una base giuridica:",
    en: "We process personal data only where there is a lawful basis:",
  },
  privacyWhyWeProcessDataProvideService: {
    // eslint-disable-next-line max-len
    de: "Bereitstellung und Administration des Dienstes (Konten anlegen, Dokumente hosten/teilen, Benachrichtigungen und E-Mails versenden, Support betreiben). Rechtsgrundlage: Vertrag (Art. 6 Abs. 1 lit. b) oder berechtigte Interessen für essentielle Servicefunktionen (Art. 6 Abs. 1 lit. f)).",
    // eslint-disable-next-line max-len
    it: "Erogazione e gestione del servizio (creazione account, hosting e condivisione documenti, invio di notifiche ed e-mail, supporto). Base giuridica: contratto (art. 6(1)(b)) o legittimo interesse per funzioni essenziali (art. 6(1)(f)).",
    // eslint-disable-next-line max-len
    en: "Provide and administer the service (create accounts, host and share documents, send notifications and emails, operate support). Legal basis: contract (Art. 6(1)(b)) or legitimate interests to run essential service functions (Art. 6(1)(f)).",
  },
  privacyWhyWeProcessDataCompliance: {
    // eslint-disable-next-line max-len
    de: "Compliance und gesetzliche Aufbewahrung im Steuer-/Beratungskontext (z. B. Antworten auf behördliche Anfragen). Rechtsgrundlage: rechtliche Verpflichtung (Art. 6 Abs. 1 lit. c)).",
    // eslint-disable-next-line max-len
    it: "Conformità e obblighi di legge in ambito fiscale/contabile (es. risposte a richieste delle autorità). Base giuridica: obbligo legale (art. 6(1)(c)).",
    // eslint-disable-next-line max-len
    en: "Compliance and record-keeping related to tax/advisory obligations (e.g., responses to lawful requests). Legal basis: legal obligation (Art. 6(1)(c)).",
  },
  privacyWhyWeProcessDataSecurity: {
    // eslint-disable-next-line max-len
    de: "Sicherheit und Betrugsprävention (Monitoring von Logs, Zugriffskontrollen, Incident Response). Rechtsgrundlage: berechtigte Interessen (Art. 6 Abs. 1 lit. f)).",
    // eslint-disable-next-line max-len
    it: "Sicurezza e prevenzione frodi (monitoraggio dei log, controlli di accesso, gestione incidenti). Base giuridica: legittimo interesse (art. 6(1)(f)).",
    // eslint-disable-next-line max-len
    en: "Security & fraud prevention (monitoring logs, access controls, incident response). Legal basis: legitimate interests (Art. 6(1)(f)).",
  },
  privacyWhyWeProcessDataImprovement: {
    // eslint-disable-next-line max-len
    de: "Serviceverbesserung und Analytik (analisi aggregata delle tendenze d’uso per migliorare affidabilità ed esperienza). Rechtsgrundlage: berechtigte Interessen (Art. 6 Abs. 1 lit. f)).",
    // eslint-disable-next-line max-len
    it: "Miglioramento del servizio e analisi (tendenze d’uso aggregate per migliorare affidabilità ed esperienza utente). Base giuridica: legittimo interesse (art. 6(1)(f)).",
    // eslint-disable-next-line max-len
    en: "Service improvement & analytics (aggregate usage trends to improve reliability and UX). Legal basis: legitimate interests (Art. 6(1)(f)).",
  },
  privacyWhyWeProcessDataDirectComms: {
    // eslint-disable-next-line max-len
    de: "Direkte Kommunikation zu Ihrem Konto oder zu Serviceänderungen (betriebliche E-Mails/Benachrichtigungen). Rechtsgrundlage: Vertrag/berechtigte Interessen.",
    // eslint-disable-next-line max-len
    it: "Comunicazioni dirette sul tuo account o su modifiche del servizio (e-mail/notifiche operative). Base giuridica: contratto/legittimo interesse.",
    // eslint-disable-next-line max-len
    en: "Direct communications about your account or service changes (operational emails/notifications). Legal basis: contract/legitimate interests.",
  },
  privacyLegitimateInterestsExplained: {
    // eslint-disable-next-line max-len
    de: "Berechtigte Interessen: Betrieb eines sicheren, zuverlässigen Mandantenportals; Missbrauchsprävention; Verbesserung zentraler Funktionen. Dabei nehmen wir eine Interessenabwägung vor.",
    // eslint-disable-next-line max-len
    it: "Interessi legittimi: mantenere un portale clienti sicuro e affidabile; prevenire abusi; migliorare le funzionalità principali, con valutazione del bilanciamento degli interessi.",
    // eslint-disable-next-line max-len
    en: "Legitimate interests explained: maintaining a secure, reliable client portal; preventing abuse; improving core features. Where we rely on legitimate interests, we balance them against your rights.",
  },
  privacySpecialCategoryNote: {
    // eslint-disable-next-line max-len
    de: "Besondere Kategorien: Wir erfragen keine besonderen Kategorien personenbezogener Daten. Falls solche Daten in Dokumenten enthalten sind, verarbeiten wir sie nur, wenn unbedingt erforderlich und gemäß Art. 9 Abs. 2 DSGVO oder anderer anwendbarer EU/italienischer Vorschriften. Bitte laden Sie solche Daten nur hoch, wenn erforderlich.",
    // eslint-disable-next-line max-len
    it: "Categorie particolari: non richiediamo categorie particolari di dati. Se tali dati sono inclusi nei documenti, li trattiamo solo se strettamente necessari e ai sensi dell’art. 9(2) GDPR o di altre norme UE/italiane applicabili. Evita di includerli se non necessario.",
    // eslint-disable-next-line max-len
    en: "Potential special-category data in uploads: We do not ask for special-category data. If such data is included in documents, we process it only where strictly necessary and permitted under GDPR Art. 9(2) or other applicable EU/Italian law. Please avoid including such data unless required.",
  },
  privacyWhoReceivesDataTitle: {
    de: "6) Wer Daten erhält",
    it: "6) Destinatari dei dati",
    en: "6) Who receives data",
  },
  privacyWhoReceivesDataInternal: {
    de: "Interne Empfänger: Befugte Mitarbeitende der PSP STP srl (z. B. Ihr Beratungsteam).",
    // eslint-disable-next-line max-len
    it: "Destinatari interni: personale autorizzato di PSP STP srl (es. il tuo team di consulenza fiscale).",
    // eslint-disable-next-line max-len
    en: "Internal recipients: authorized staff at PSP STP srl who need access to serve you (e.g., your tax advisor team).",
  },
  privacyWhoReceivesDataProcessors: {
    // eslint-disable-next-line max-len
    de: "Auftragsverarbeiter (Dienstleister): Hosting, E-Mail-Versand, Support-Tools, Logging/Monitoring, Dokumentenvorschau – jeweils auf Grundlage von Auftragsverarbeitungsverträgen.",
    // eslint-disable-next-line max-len
    it: "Responsabili del trattamento (fornitori): hosting sicuro, invio e-mail, strumenti di assistenza, logging/monitoraggio, servizi di anteprima documenti — vincolati da accordi di nomina.",
    // eslint-disable-next-line max-len
    en: "Processors (service providers): secure hosting, email delivery, customer support tools, logging/monitoring, and document preview services—bound by data-processing agreements.",
  },
  privacyWhoReceivesDataAuthorities: {
    de: "Behörden oder Dritte: wenn gesetzlich erforderlich (z. B. Gerichts-/Behördenanfragen).",
    // eslint-disable-next-line max-len
    it: "Autorità o terzi: quando previsto dalla legge (es. richieste dell’autorità giudiziaria/amministrativa).",
    en: "Authorities or third parties: when legally required (e.g., court/authority requests).",
  },
  privacyInternationalTransfersTitle: {
    de: "7) Internationale Datenübermittlungen",
    it: "7) Trasferimenti internazionali",
    en: "7) International transfers",
  },
  privacyInternationalTransfersBody: {
    // eslint-disable-next-line max-len
    de: "Falls Anbieter Daten außerhalb des EWR verarbeiten oder darauf zugreifen, nutzen wir rechtmäßige Übermittlungsmechanismen — z. B. EU-Angemessenheitsbeschlüsse oder Standardvertragsklauseln (SCC) mit ergänzenden Maßnahmen — und stellen auf Anfrage eine Kopie bzw. einen Verweis bereit.",
    // eslint-disable-next-line max-len
    it: "Se un fornitore conserva o accede ai dati al di fuori dello SEE, adottiamo meccanismi leciti di trasferimento — ad es. decisioni di adeguatezza dell’UE o Clausole Contrattuali Standard (SCC) con misure supplementari — e possiamo fornire una copia/riferimento su richiesta.",
    // eslint-disable-next-line max-len
    en: "If any provider stores or accesses data outside the EEA, we ensure a lawful transfer mechanism—e.g., an EU adequacy decision or Standard Contractual Clauses (SCCs) with supplementary measures—and can provide a copy or reference upon request.",
  },
  privacyRetentionTitle: {
    de: "8) Speicherdauer",
    it: "8) Periodi di conservazione",
    en: "8) How long we keep data",
  },
  privacyRetentionBody: {
    // eslint-disable-next-line max-len
    de: "Wir speichern personenbezogene Daten nur so lange, wie es für die genannten Zwecke erforderlich ist, einschließlich gesetzlicher Aufbewahrungsfristen im Steuer-/Rechnungswesen. Anschließend löschen oder anonymisieren wir sie sicher. Konkrete Fristen sind in unserem internen Plan festgelegt und auf Anfrage erhältlich.",
    // eslint-disable-next-line max-len
    it: "Conserviamo i dati personali solo per il tempo necessario alle finalità sopra indicate, inclusi i termini di legge in materia fiscale/contabile. Successivamente li cancelliamo o li anonimizzatiamo in modo sicuro. I periodi esatti sono definiti nel nostro piano interno e disponibili su richiesta.",
    // eslint-disable-next-line max-len
    en: "We keep personal data only as long as necessary for the purposes above, including statutory retention under tax/accounting laws. We then securely delete or anonymize it. Exact retention periods are listed in our internal schedule and available on request.",
  },
  privacyYourRightsTitle: {
    de: "9) Ihre Rechte",
    it: "9) I tuoi diritti",
    en: "9) Your rights",
  },
  privacyYourRightsBody: {
    // eslint-disable-next-line max-len
    de: "Sie können Auskunft, Berichtigung, Löschung, Einschränkung, Datenübertragbarkeit und Widerspruch verlangen (einschließlich eines besonderen Widerspruchsrechts gegen Direktwerbung). Zudem können Sie eine erteilte Einwilligung jederzeit widerrufen. Wir erläutern etwaige Entscheidungen und Ihre Optionen.",
    // eslint-disable-next-line max-len
    it: "Puoi esercitare i diritti di accesso, rettifica, cancellazione, limitazione, portabilità e opposizione (compreso un diritto specifico di opporti al marketing diretto). Hai inoltre il diritto di revocare il consenso. Ti spiegheremo eventuali decisioni e le tue opzioni.",
    // eslint-disable-next-line max-len
    en: "You can request access, rectification, erasure, restriction, portability, and objection (including a specific right to object to direct marketing). You also have the right to withdraw consent where processing is based on consent. We will explain any decision-making and your options.",
  },
  privacyExerciseRightsBody: {
    // eslint-disable-next-line max-len
    de: "Wie Sie Ihre Rechte ausüben: Schreiben Sie an info@psp-bz.it. Sie haben außerdem das Recht, Beschwerde bei der italienischen Datenschutzaufsicht (Garante per la protezione dei dati personali) einzulegen. Siehe die Website des Garante.",
    // eslint-disable-next-line max-len
    it: "Come esercitarli: scrivi a info@psp-bz.it. Hai anche il diritto di proporre reclamo al Garante per la protezione dei dati personali. Consulta il sito del Garante.",
    // eslint-disable-next-line max-len
    en: "How to exercise your rights: email info@psp-bz.it. You also have the right to lodge a complaint with the Italian Data Protection Authority (Garante per la protezione dei dati personali). See the Garante’s website.",
  },
  privacyAutomatedDecisionMakingTitle: {
    de: "10) Automatisierte Entscheidungen",
    it: "10) Decisioni automatizzate",
    en: "10) Automated decision-making",
  },
  privacyAutomatedDecisionMakingBody: {
    // eslint-disable-next-line max-len
    de: "Wir treffen keine ausschließlich automatisierten Entscheidungen mit Rechtswirkung oder ähnlich erheblicher Beeinträchtigung. Sollten wir dies künftig tun, informieren wir über die zugrunde liegende Logik, die Tragweite und Ihre Rechte.",
    // eslint-disable-next-line max-len
    it: "Non effettuiamo decisioni basate unicamente su trattamenti automatizzati che producano effetti giuridici o similmente significativi. Se in futuro lo facessimo, ti informeremo sulla logica sottesa, sulle conseguenze e sui tuoi diritti.",
    // eslint-disable-next-line max-len
    en: "We do not make decisions based solely on automated processing that produce legal or similarly significant effects. If we ever do, we will inform you of the logic involved and consequences, and your rights related to it.",
  },
  privacyDoYouHaveToProvideDataTitle: {
    de: "11) Müssen Sie Daten bereitstellen?",
    it: "11) Sei obbligato a fornire i dati?",
    en: "11) Do you have to provide data?",
  },
  privacyDoYouHaveToProvideDataBody: {
    // eslint-disable-next-line max-len
    de: "Bestimmte Angaben sind zur Kontoerstellung/-führung und zur Erbringung unserer Leistungen erforderlich. Ohne diese Daten können wir den Dienst ggf. nicht bereitstellen oder Anfragen nicht beantworten (Art. 13 Abs. 2 lit. e DSGVO).",
    // eslint-disable-next-line max-len
    it: "Alcune informazioni sono necessarie per creare e gestire l’account e per erogare i servizi. In mancanza, potremmo non essere in grado di fornire il servizio o rispondere alle richieste (art. 13(2)(e) GDPR).",
    // eslint-disable-next-line max-len
    en: "Some information is necessary to create and maintain your account and to deliver advisory services. If you do not provide required data, we may be unable to provide the service or respond to requests (Art. 13(2)(e)).",
  },
  privacySecurityTitle: {
    de: "12) Sicherheit",
    it: "12) Sicurezza",
    en: "12) Security",
  },
  privacySecurityBody: {
    // eslint-disable-next-line max-len
    de: "Wir setzen angemessene technische und organisatorische Maßnahmen um (z. B. strikte Zugriffskontrollen, Verschlüsselung ruhender Daten, soweit anwendbar, Sicherheitsmonitoring). Passwörter speichern wir als Einweg-Hashes mit modernen Algorithmen (z. B. bcrypt).",
    // eslint-disable-next-line max-len
    it: "Applichiamo misure tecniche e organizzative adeguate al rischio (ad es. rigorosi controlli di accesso, crittografia dei dati a riposo quando applicabile, monitoraggio della sicurezza). Le password sono memorizzate come hash unidirezionali con algoritmi moderni (es. bcrypt).",
    // eslint-disable-next-line max-len
    en: "We apply organizational and technical measures appropriate to risk (e.g., strict access controls, encryption at rest where applicable, security monitoring). Passwords are stored as one-way hashes using modern algorithms (e.g., bcrypt).",
  },
  privacyImportedDataTitle: {
    de: "13) Wenn Ihre Daten importiert wurden (Art. 14 DSGVO)",
    it: "13) Se i tuoi dati sono stati importati (art. 14 GDPR)",
    en: "13) If your data was imported (not collected from you directly)",
  },
  privacyImportedDataBody: {
    // eslint-disable-next-line max-len
    de: "Wenn Ihre Kontodaten aus internen Systemen der PSP STP srl importiert wurden, stellen wir Ihnen diese Informationen innerhalb eines Monats nach Erhalt, bei der ersten Kommunikation mit Ihnen oder vor der ersten Offenlegung gegenüber Empfängern zur Verfügung – je nachdem, was zuerst eintritt. Wir teilen Ihnen auch die Datenkategorien und die Quelle mit.",
    // eslint-disable-next-line max-len
    it: "Se i tuoi dati di account sono stati importati dai sistemi interni di PSP STP srl, ti forniamo queste informazioni entro un mese dall’ottenimento, alla prima comunicazione con te o prima della prima comunicazione a destinatari — a seconda di quale evento avvenga per primo. Indicheremo anche le categorie di dati e la fonte.",
    // eslint-disable-next-line max-len
    en: "When your account data was imported from PSP STP srl’s internal systems, we will provide you this information within one month of obtaining your data, or at first communication with you, or before first disclosure to any recipient—whichever comes first. We will also tell you the categories of personal data and the source.",
  },
  privacyChangesTitle: {
    de: "14) Änderungen dieser Erklärung",
    it: "14) Modifiche alla presente informativa",
    en: "14) Changes to this notice",
  },
  privacyChangesBody: {
    // eslint-disable-next-line max-len
    de: "Wir können diese Erklärung an Gesetzesänderungen oder unsere Verarbeitung anpassen. Wesentliche Änderungen teilen wir Ihnen mit und veröffentlichen sie hier.",
    // eslint-disable-next-line max-len
    it: "Potremo aggiornare questa informativa per riflettere modifiche normative o del trattamento. Pubblicheremo gli aggiornamenti qui e ti informeremo in caso di cambiamenti significativi.",
    // eslint-disable-next-line max-len
    en: "We may update this notice to reflect changes in law or our processing. We will post updates here and notify you of significant changes.",
  },
  localStorage: {
    de: "localStorage",
    it: "localStorage",
    en: "lolStorage",
  },
  accessTokenPurpose: {
    de: "Zugriffstoken zur Authentifizierung von API-Aufrufen und für die Persistenz der Sitzung.",
    it: "Token di accesso per autenticare le chiamate API e mantenere la sessione.",
    en: "Access token used to authenticate API requests and keep the session active.",
  },
  untilLogoutOrClear: {
    de: "Bis Logout/Löschung",
    it: "Fino a logout/cancellazione",
    en: "Until logout/clear",
  },
  untilClear: {
    de: "Bis Löschung",
    it: "Fino alla cancellazione",
    en: "Until clear",
  },
  untilLogoutClearOrExpiry: {
    de: "Bis zum Ablauf oder Logout/Löschung",
    it: "Fino a scadenza o logout/cancellazione",
    en: "Until logout/clear or expiry",
  },
  refreshTokenPurpose: {
    de: "Refresh-Token zum Erneuern von Access-Tokens ohne erneute Anmeldung.",
    it: "Refresh token per ottenere nuovi accesss token senza nuovo login.",
    en: "Refresh token used to obtain new access tokens without re-login.",
  },
  cookieConsentPurpose: {
    // eslint-disable-next-line max-len
    de: "Merkt sich Ihre Einwilligungs-Auswahl (damit der Banner nicht jedes Mal erneut erscheint).",
    it: "Memorizza le tue scelte di consenso (per non riproporre il banner ogni volta).",
    en: "Remembers your consent choices (so we don't ask every time).",
  },
  msalVersionPurpose: {
    de: "Bibliotheks-/Versionsmarker für zuverlässigen Login.",
    it: "Indicatore libreria/versione per un login affidabile.",
    en: "Library/version marker used for reliable sign-in flow.",
  },
  selectedMandate: {
    de: "Mandat merken, auf diesem Gerät, damit es beim nächsten Mal vorausgewählt ist.",
    it: "Ricordare il mandato su questo dispositivo per pre-selezionarlo al prossimo accesso.",
    en: "Remember mandate on this device so it's pre-selected next time.",
  },
  selectedMandateRetention: {
    de: "Bis Mandatwechsel, Widerruf oder Löschung.",
    it: "Finché non cambi mandato, revochi il consenso o cancelli.",
    en: "Until you change mandate, withdraw consent, or clear storage.",
  },
  functionalCookies: {
    de: "Funktionale Cookies",
    it: "Cookie funzionali",
    en: "Functional cookies",
  },
  essentialCookies: {
    de: "Essentielle Cookies",
    it: "Cookie essenziali",
    en: "Essential cookies",
  },
  optionalCookies: {
    de: "Optionale Cookies",
    it: "Cookie opzionali",
    en: "Optional cookies",
  },
  cookies: {
    de: "Cookies",
    it: "Cookie ",
    en: "Cookies",
  },
  cookieName: {
    de: "Name (Key)",
    it: "Nome (key)",
    en: "Name (key)",
  },
  retention: {
    de: "Aufbewahrung",
    it: "Conservazione",
    en: "Retention",
  },
  riepilogo_ricavi: {
    de: "Umsatzübersicht",
    it: "Riepilogo ricavi",
    en: "Revenue summary",
  },
  BILAQ: {
    de: "BILAQ",
    it: "BILAQ",
    en: "BILAQ",
  },
  preview: {
    de: "Vorschau",
    it: "Anteprima",
    en: "Preview",
  },
  sendEmail: {
    de: "Email senden",
    it: "Invia email",
    en: "Send email",
  },
  sendPush: {
    de: "Push senden",
    it: "Invia push",
    en: "Send push",
  },
  pleaseSelectMandate: {
    // eslint-disable-next-line max-len
    de: "Du hast kein Mandat ausgewählt. Um diese Daten einzusehen, musst du ein Mandat ausgewählt haben. Bitte wähle zuerst eines aus.",
    // eslint-disable-next-line max-len
    it: "Non hai selezionato alcun mandato. Per visualizzare questi dati, devi aver selezionato un mandato. Per favore, selezionane prima uno.",
    // eslint-disable-next-line max-len
    en: "You haven't selected a mandate. To view this data, you must have selected a mandate. Please select one first.",
  },
  cantDeleteMovedInboundDocument: {
    de: "Du kannst nicht ein Eingangsdokument löschen, was schon verschoben worden ist.",
    it: "Non puoi eliminare un documento in entrata che è già stato spostato.",
    en: "You can't delete an already moved inbound document.",
  },
  areYouSureDeleteInboundDocument: {
    // eslint-disable-next-line max-len
    de: "Du bist dabei, dieses Eingangsdokument zu löschen. Dieser Vorgang kann nicht rückgängig gemacht werden. Bist du dir sicher?",
    // eslint-disable-next-line max-len
    it: "Stai per eliminare permanentemente questo documento in entrata. Questa azione non può essere annullata. Sei sicuro?",
    en: "You're about to delete this inbound document. This action cannot be undone. Are you sure?",
  },
  documentUploadedSuccessfully: {
    de: "Dokument erfolgreich hochgeladen ({documentName}).",
    it: "Documento caricato con successo ({documentName}).",
    en: "Document uploaded successfully ({documentName}).",
  },
  documentsUploadedSuccessfully: {
    // eslint-disable-next-line max-len
    de: "Dokumente erfolgreich hochgeladen ({documentNames}).\nDas Importieren kann ein paar Minuten dauern.",
    // eslint-disable-next-line max-len
    it: "Documenti caricato con successo ({documentNames}).\nL'importazione può durare qualche minuto.",
    // eslint-disable-next-line max-len
    en: "Documents uploaded successfully ({documentNames}).\nThe import can take a couple of minutes.",
  },
  inboundDocumentDeletedSuccessfully: {
    de: "Eingangsdokument erfolgreich gelöscht ({documentName}).",
    it: "Documento in entrata eliminato con successo ({documentName}).",
    en: "Inbound document deleted successfully ({documentName}).",
  },
  year: {
    de: "Jahr",
    it: "Anno",
    en: "Year",
  },
  month: {
    de: "Monat",
    it: "Mese",
    en: "Month",
  },
  sendByMandate: {
    de: "An Mandate schicken",
    it: "Invia a mandati",
    en: "Send to mandates",
  },
  searchAMandate: {
    de: "Suche ein Mandat",
    it: "Cerca un mandato",
    en: "Search a mandate",
  },
  privacy: {
    de: "Datenschutz",
    it: "Privacy",
    en: "Privacy",
  },
  version: {
    de: "Version",
    it: "Versione",
    en: "Version",
  },
  versions: {
    de: "Versionen",
    it: "Versioni",
    en: "Versions",
  },
  pastVersions: {
    de: "Vergangene Versionen",
    it: "Versioni passate",
    en: "Past versions",
  },
  restoreVersionMessage: {
    de: "Zu einem früheren Bearbeitungszustand zurückkehren",
    it: "Torna a una versione precedente",
    en: "Restore a previous version",
  },
  restoreVersionConfirmationMessage: {
    de: "Du bist dabei, einen Bearbeitungszustand vom {date} wiederherzustellen. Bist du sicher?",
    it: "Stai per ripristinare una versione del {date}. Sei sicuro?",
    en: "You're about to restore a version from {date}. Are you sure?",
  },
  restoreOriginalMessage: {
    de: "Alle Änderungen verwerfen und den Ausgangszustand laden",
    it: "Annulla tutte le modifiche e ripristina lo stato originale",
    en: "Discard all changes and restore the original state",
  },
  unsupportedFiletype: {
    de: "Dateientyp wird nicht unterstützt.",
    it: "Estensione del file non supportata.",
    en: "Filetype not supported.",
  },
  createdBy: {
    de: "Erstellt von",
    it: "Creata da",
    en: "Created by",
  },
  folders: {
    de: "Ordner",
    it: "Cartelle",
    en: "Folders",
  },
  composeAMessage: {
    de: "Schreibe eine Nachricht...",
    it: "Scrivi un messaggio...",
    en: "Compose a message...",
  },
  subject: {
    de: "Betreff",
    it: "Oggetto",
    en: "Subject",
  },
  close: {
    de: "Schließen",
    it: "Chiudi",
    en: "Close",
  },
  dropOrSelectFiles: {
    de: "Dateien hier ablegen oder auswählen",
    it: "Trascina i file qui o seleziona",
    en: "Drop files here or select",
  },
  dropOrSelectFile: {
    de: "Datei hier ablegen oder auswählen",
    it: "Trascina il file qui o seleziona",
    en: "Drop file here or select",
  },
  selectFile: {
    de: "Datei hier auswählen",
    it: "Seleziona il file qui",
    en: "Select file here",
  },
  addNote: {
    de: "Notiz hinzufügen",
    it: "Aggiungi nota",
    en: "Add note",
  },
  note: {
    de: "Notiz",
    it: "Nota",
    en: "Note",
  },
  noteWarning: {
    // eslint-disable-next-line max-len
    de: "Durch das Hinzufügen einer Notiz wird Ihr Buchhalter bzw. Ihre Lohnbuchhaltung automatisch benachrichtigt.",
    // eslint-disable-next-line max-len
    it: "Aggiungendo una nota, il tuo contabile o contabile delle paghe verrà automaticamente notificato.",
    en: "By adding a note, your accountant or payroll department will be notified automatically.",
  },
  notificationCouldNotBeMarkedAsRead: {
    de: "Die Benachrichtigung konnte nicht als gelesen markiert werden.",
    it: "Non è stato possibile contrassegnare la notifica come letta.",
    en: "The notification could not be marked as read.",
  },
  markAllAsRead: {
    de: "Alle als gelesen markieren",
    it: "Contrassegna tutte come lette",
    en: "Mark all as read",
  },
  templatesAndInstructions: {
    de: "Vorlagen & Anleitungen",
    it: "Modelli e istruzioni",
    en: "Templates & instructions",
  },
  subfolders: {
    de: "Unterordner",
    it: "Sottocartelle",
    en: "Subfolders",
  },
  determineWhetherUserEmployeeNotifications: {
    // eslint-disable-next-line max-len
    de: "Lege fest, ob dieser Mitarbeiter eine Benachrichtigung erhalten soll, wenn ein Dokument zu einem Mandate hochgeladen wird.",
    // eslint-disable-next-line max-len
    it: "Determina se l'impiegato deve ricevere una notifica quando viene caricato un documento su un mandato.",
    // eslint-disable-next-line max-len
    en: "Determine whether the employee should receive a notification whenever a document is uploaded to a mandate.",
  },
  notificationSettings: {
    de: "Benachrichtigungseinstellungen",
    it: "Impostazioni notifiche",
    en: "Notification settings",
  },
  push: {
    de: "Push-Nachricht",
    it: "Notifica push",
    en: "Push notification",
  },
  needHelp: {
    de: "Brauchst du Hilfe?",
    it: "Serve aiuto?",
    en: "Need help?",
  },
  contactUs: {
    de: "Kontaktiere uns",
    it: "Contattaci",
    en: "Contact us",
  },
  message: {
    de: "Nachricht",
    it: "Messaggio",
    en: "Message",
  },
  messageSentSuccessfully: {
    de: "Nachricht erfolgreich abgesendet.",
    it: "Messaggio inviato con successo.",
    en: "Message sent successfully.",
  },
  dropFileHere: {
    de: "Lege die Datei hier ab",
    it: "Trascina il file qui",
    en: "Drop the file here",
  },
  dropFilesHere: {
    de: "Lege die Dateien hier ab",
    it: "Trascina i file qui",
    en: "Drop the files here",
  },
  searching: {
    de: "Suche...",
    it: "Cerca...",
    en: "Searching...",
  },
  noResultsFound: {
    de: "Keine Ergebnisse gefunden.",
    it: "Nessun risultato trovato.",
    en: "No results found.",
  },
  label: {
    de: "Bezeichnung",
    it: "Denominazione",
    en: "Label",
  },
  labelDe: {
    de: "Bezeichnung (Deutsch)",
    it: "Denominazione (Tedesco)",
    en: "Label (German)",
  },
  labelIt: {
    de: "Bezeichnung (Italienisch)",
    it: "Denominazione (Italiano)",
    en: "Label (Italian)",
  },
  labelEn: {
    de: "Bezeichnung (Englisch)",
    it: "Denominazione (Inglese)",
    en: "Label (English)",
  },
  selectDocumentTypesForRightNotice: {
    de: "Wähle aus, auf welche Dokumentypen, Nutzer mit diesem Recht Zugriff haben.",
    it: "Scegli a quali tipi di documenti gli utenti con questo diritto possono accedere.",
    en: "Select which document types users with this right can access.",
  },
  folderAccess: {
    de: "Ordnerzugriff",
    it: "Accesso alle cartelle",
    en: "Folder access",
  },
  folderAccessNotice: {
    de: "Wähle aus, auf welche Ordner Nutzer mit diesem Recht zugriff haben.",
    it: "Scegli a quali cartelle gli utenti con questo diritto possono accedere.",
    en: "Select which folders users with this right can access.",
  },
  "01 Belege": {
    de: "01 Belege",
    it: "01 Documenti contabili",
    en: "01 Vouchers",
  },
  "02 Banken, Kassa, Kreditkarte": {
    de: "02 Banken, Kassa, Kreditkarte",
    it: "02 Banche, Cassa, Carta di credito",
    en: "02 Banks, Cash, Credit Card",
  },
  "03 Rechnungen + Tagesinkassi": {
    de: "03 Rechnungen + Tagesinkassi",
    it: "03 Fatture + Incasso giornaliero",
    en: "03 Invoices + Daily Cash Collections",
  },
  "04 Lohnbelege": {
    de: "04 Lohnbelege",
    it: "04 Documenti salariali",
    en: "04 Payroll Documents",
  },
  "05 Sonstige Unterlagen - Meldungen": {
    de: "05 Sonstige Unterlagen - Meldungen",
    it: "05 Altri documenti - Comunicazioni",
    en: "05 Other Documents - Notifications",
  },
  "06 noch zu erledigen": {
    de: "06 Noch zu erledigen",
    it: "06 Ancora da fare",
    en: "06 Still to be done",
  },
  test: {
    de: "TEST",
    it: "TEST",
    en: "TEST",
  },
  "01 CU": {
    de: "01 CU",
    it: "01 CU",
    en: "01 CU",
  },
  "02 IVA JE": {
    de: "02 MwSt. JE",
    it: "02 IVA JE",
    en: "02 VAT JE",
  },
  "03 Reddito": {
    de: "03 Einkommenssteuer",
    it: "03 Reddito",
    en: "03 Income Tax",
  },
  onlyPdfsSupported: {
    de: "Nur PDFs werden unterstützt.",
    it: "Vengono supportati solo file PDF.",
    en: "Only PDF files are supported.",
  },
  addExternalUserToMandate: {
    de: "Externen Benutzer zu Mandat hinzufügen",
    it: "Aggiungi utente esterno a mandato",
    en: "Add external user to mandate",
  },
  addUserToMandate: {
    de: "Benutzer zu Mandat hinzufügen",
    it: "Aggiungi utente al mandato",
    en: "Add user to mandate",
  },
  rightDoesNotExist: {
    de: "Dieses Recht existiert nicht.",
    it: "Questo diritto non esiste.",
    en: "This right does not exist.",
  },
  changesToRightsUpTo10Minutes: {
    de: "Änderungen an Rechten können bis zu 10 Minuten dauern, bis sie wirksam werden.",
    it: "Le modifiche ai permessi possono richiedere fino a 10 minuti per entrare in vigore.",
    en: "Changes to rights may take up to 10 minutes to take effect.",
  },
  selectAll: {
    de: "Alle auswählen",
    it: "Seleziona tutti",
    en: "Select all",
  },
  deselectAll: {
    de: "Keine auswählen",
    it: "Deseleziona tutti",
    en: "Deselect all",
  },
  userDoesNotExist: {
    de: "Dieser Benutzer existiert nicht.",
    it: "Questo utente non esiste.",
    en: "This user does not exist.",
  },
  userDeleteSuccess: {
    de: "Benutzer erfolgreich gelöscht.",
    it: "Utente eliminato con successo.",
    en: "User deleted successfully.",
  },
  youAreAboutToDeleteUser: {
    de: "Du bist dabei, diesen Benutzer permanent zu entfernen. Bist du dir sicher?",
    it: "Stai per eliminare permanentemente questo utente. Sei sicuro?",
    en: "You are about to permanently delete this user. Are you sure?",
  },
  noMessagesNotice: {
    de: "Momentan hast du noch keine Nachrichten.",
    it: "Attualmente non ci sono messaggi.",
    en: "There are currently no messages.",
  },
  noSearchResultsFound: {
    de: "Es wurden keine Suchergebnisse gefunden.",
    it: "Non è stato trovato alcun risultato di ricerca.",
    en: "No search result has been found.",
  },
  finish: {
    de: "Fertig",
    it: "Finito",
    en: "Finish",
  },
  maybeLater: {
    de: "Vielleicht später",
    it: "Forse dopo",
    en: "Maybe later",
  },
  setup2FaInitialTitle: {
    de: "2-Faktor-Authentifizierung einrichten?",
    it: "Configurare l'autenticazione a due fattori?",
    en: "Set up two-factor authentication?",
  },
  setup2FaInitialBody: {
    de: "Möchtest du die Zwei-Faktor-Authentifizierung für deinen Account einrichten?",
    it: "Vuoi configurare l'autenticazione a due fattori per il tuo account?",
    en: "Would you like to set up two-factor authentication for your account?",
  },
  setup2FaTitle: {
    de: "2-Faktor-Authentifizierung einrichten",
    it: "Configurazione autenticazione a due fattori",
    en: "Set up two-factor authentication",
  },
  setup2FaShort: {
    de: "2FA einrichten",
    it: "Configura 2FA",
    en: "Set up 2FA",
  },
  mfaScanAuthenticatorQrBody: {
    // eslint-disable-next-line max-len
    de: "Scanne den QR-Code mit deiner bevorzugten Authenticator-App oder gib den Setup-Schlüssel manuell ein.",
    // eslint-disable-next-line max-len
    it: "Scansiona il codice QR con l'app di autenticazione che preferisci, oppure inserisci la chiave di configurazione manualmente.",
    en: "Scan the QR code with your authenticator app of choice, or use the setup key manually.",
  },
  mfaAuthenticatorSetupMobile: {
    de: "Kopiere den Setup-Schlüssel und füge ihn in deiner Authenticator-App hinzu.",
    it: "Copia la chiave di configurazione e aggiungila nella tua app di autenticazione.",
    en: "Copy the setup key and add it to your authenticator app.",
  },
  orPreposition: {
    de: "oder",
    it: "o",
    en: "or",
  },
  totpSecret: {
    de: "TOTP-Schlüssel",
    it: "Chiave TOTP",
    en: "TOTP secret",
  },
  couldntCopySecret: {
    de: "TOTP-Schlüssel konnte nicht kopiert werden.",
    it: "Non è stato possibile copiare la chiave TOTP.",
    en: "Could not copy TOTP secret.",
  },
  totpCode: {
    de: "Einmalpasswort (TOTP)",
    it: "Codice TOTP",
    en: "TOTP code",
  },
  selectOptionToContinue: {
    de: "Wähle eine Option, um fortzufahren",
    it: "Seleziona un'opzione per continuare.",
    en: "Select an option to continue.",
  },
  selectOption: {
    de: "Wähle eine Option aus",
    it: "Seleziona un'opzione",
    en: "Select an option",
  },
  totpPlaceholder: {
    de: "XXX XXX",
    it: "XXX XXX",
    en: "XXX XXX",
  },
  twoFactorAuthSetupSuccessfully: {
    de: "Zwei-Faktor-Authentifizierung erfolgreich eingerichtet",
    it: "Autenticazione a due fattori configurata con successo",
    en: "Two-factor authentication has been set up successfully",
  },
  successfullySetUp: {
    de: "Erfolgreich eingerichtet",
    it: "Configurato con successo",
    en: "Successfully set up",
  },
  login: {
    de: "Anmelden",
    it: "Entra",
    en: "Login",
  },
  enterEmailToContinue: {
    de: "Gib deine E-Mail Adresse ein um fortzufahren",
    it: "Inserisci il tuo indirizzo e-mail per continuare",
    en: "Enter your email address to continue",
  },
  pleaseEnterPassword: {
    de: "Bitte gib dein Passwort ein",
    it: "Per favore inserisci la tua password",
    en: "Please enter your password",
  },
  trustThisDevice: {
    de: "Diesem Gerät vertrauen",
    it: "Considera attendibile questo dispositivo",
    en: "Trust this device",
  },
  enterTotpOf: {
    // eslint-disable-next-line quotes
    de: 'Gib das Einmalpasswort (TOTP) von "{authenticatorLabel}" ein.',
    // eslint-disable-next-line quotes
    it: 'Inserisci il codice TOTP di "{authenticatorLabel}".',
    // eslint-disable-next-line quotes
    en: 'Enter the TOTP code of "{authenticatorLabel}"',
  },
  twoFactorAuth: {
    de: "Zwei-Faktor-Authentifizierung",
    it: "Autenticazione a due fattori",
    en: "Two-factor authentication",
  },
  twoFactorAuthIs: {
    de: "Zwei-Faktor-Authentifizierung ist",
    it: "L'Autenticazione a due fattori è",
    en: "Two-factor authentication is",
  },
  notEnabled: {
    de: "Nicht aktiv",
    it: "Disattivato",
    en: "Not enabled",
  },
  notEnabledLowercaseFeminine: {
    de: "nicht aktiv",
    it: "disattivata",
    en: "not enabled",
  },
  enabled: {
    de: "Aktiv",
    it: "Attivata",
    en: "Enabled",
  },
  enable2faForExtraSecurity: {
    // eslint-disable-next-line max-len
    de: "Aktiviere die Zwei-Faktor-Authentifizierung (2FA), um deinem Konto eine zusätzliche Sicherheitsebene hinzuzufügen.",
    // eslint-disable-next-line max-len
    it: "Attiva l’autenticazione a due fattori (2FA) per aggiungere un ulteriore livello di sicurezza al tuo account.",
    en: "Enable two-factor authentication (2FA) to add an extra layer of security to your account.",
  },
  couldntLoadMfaMethods: {
    de: "Zwei-Faktor-Authentifizierungsmethoden konnten nicht geladen werden.",
    it: "Non è stato possibile caricare i metodi per l'autenticazione a due fattori.",
    en: "Couldn't load two-factor authentication methods.",
  },
  authenticatorApp: {
    de: "Authenticator-App",
    it: "App di autenticazione",
    en: "Authenticator app",
  },
  youAreAboutToDelete2faMethod: {
    // eslint-disable-next-line max-len, quotes
    de: 'Du bist dabei, die Zwei-Faktor-Authentifizierungsmethode "{methodLabel}" zu entfernen. Bist du sicher?',
    // eslint-disable-next-line quotes
    it: 'Stai per rimuovere il metodo di autenticazione a due fattori "{methodLabel}". Sei sicuro?',
    // eslint-disable-next-line max-len, quotes
    en: 'You are about to remove the two-factor authentication method "{methodLabel}". Are you sure?',
  },
  mfaMethodDeletedSuccessfully: {
    // eslint-disable-next-line quotes
    de: 'Die Zwei-Faktor-Authentifizierungsmethode "{methodLabel}" wurde erfolgreich entfernt.',
    // eslint-disable-next-line quotes
    it: 'Il metodo di autenticazione a due fattori "{methodLabel}" è stato rimosso con successo.',
    // eslint-disable-next-line quotes
    en: 'Two-factor authentication method "{methodLabel}" has been removed successfully.',
  },
  editMfaMethod: {
    de: "Zwei-Faktor-Authentifizierungsmethode bearbeiten",
    it: "Modifica metodo di autenticazione a due fattori",
    en: "Edit two-factor authentication method",
  },
  couldntDelete2faMethod: {
    de: "Die Zwei-Faktor-Authentifizierungsmethode konnte nicht gelöscht werden.",
    it: "Impossibile eliminare il metodo di autenticazione a due fattori.",
    en: "Couldn’t delete the two-factor authentication method.",
  },
  youCannotDeleteTheLastMfaMethod: {
    de: "Du kannst nicht die letzte Zwei-Faktor-Authentifizierungsmethode löschen.",
    it: "Non puoi eliminare l'ultimo metodo di autenticazione a due fattori.",
    en: "You cannot delete the last two-factor authentication method.",
  },
  youCannotDeletePrimaryMfaMethod: {
    // eslint-disable-next-line max-len
    de: "Du kannst nicht die primäre Zwei-Faktor-Authentifizierungsmethode löschen. Lege zuerst eine andere Methode als primär fest.",
    // eslint-disable-next-line max-len
    it: "Non puoi eliminare il metodo di autenticazione a due fattori principale. Imposta prima un altro metodo come principale.",
    // eslint-disable-next-line max-len
    en: "You cannot delete the primary two-factor authentication method. First set another method as the primary one.",
  },
  addMethod: {
    de: "Methode hinzufügen",
    it: "Aggiungi metodo",
    en: "Add method",
  },
  primaryMethod: {
    de: "Primäre Methode",
    it: "Metodo principale",
    en: "Primary method",
  },
  mfaMethodEditedSuccessfully: {
    de: "Methode erfolgreich bearbeitet.",
    it: "Metodo modificato con successo.",
    en: "Method updated successfully.",
  },
  weSentEmailCodeTo: {
    de: "Wir haben dir soeben einen 6 Stelligen Code an {email} gesendet.",
    it: "Ti abbiamo appena inviato un codice a 6 cifre a {email}.",
    en: "We have just sent you a 6-digit code to {email}.",
  },
  sendCodeAgain: {
    de: "Code erneut senden",
    it: "Invia nuovamente il codice",
    en: "Resend code",
  },
  safest: {
    de: "Am sichersten",
    it: "Più sicuro",
    en: "Safest",
  },
  selectMfaMethod: {
    de: "Wähle eine Methode für deine Zwei-Faktor-Authentifizierung.",
    it: "Choose a method for your two-factor authentication.",
    en: "Scegli un metodo per la tua autenticazione a due fattori.",
  },
  alreadyHaveEmailMethod: {
    // eslint-disable-next-line max-len
    de: "Eine E-Mail-basierte Zwei-Faktor-Authentifizierungsmethode ist bereits mit diesem Konto verknüpft. Es kann nur eine eingerichtet werden.",
    // eslint-disable-next-line max-len
    it: "Un metodo di autenticazione a due fattori basato su email è già associato a questo account. Puoi configurarne solo uno.",
    // eslint-disable-next-line max-len
    en: "An email-based two-factor authentication method is already linked to this account. Only one email-based method can be set up.",
  },
  emailCode: {
    de: "Email-Code",
    it: "Codice email",
    en: "Email code",
  },
  manageTemplates: {
    de: "Vorlagen verwalten",
    it: "Gestisci modelli",
    en: "Manage templates",
  },
  newTemplate: {
    de: "Neue Vorlage",
    it: "Nuovo modello",
    en: "New template",
  },
  templateTitle: {
    de: "Vorlagentitel",
    it: "Titolo modello",
    en: "Template title",
  },
  templateType: {
    de: "Vorlagenart",
    it: "Tipo di modello",
    en: "Template type",
  },
  youAreAboutToDeleteTemplate: {
    // eslint-disable-next-line quotes
    de: 'Du bist dabei, die Vorlage "{templateName}" zu löschen. Bist du dir sicher?',
    // eslint-disable-next-line quotes
    it: 'Stai per eliminare il modello "{templateName}". Sei sicuro?',
    // eslint-disable-next-line quotes
    en: 'You are about to delete the template "{templateName}". Are you sure?',
  },
  adminPanel: {
    de: "Admin Panel",
    it: "Pannello admin",
    en: "Admin panel",
  },
  activeTasks: {
    de: "Aktive Tasks",
    it: "Task attive",
    en: "Active tasks",
  },
  tasks: {
    de: "Tasks",
    it: "Task",
    en: "Tasks",
  },
  fullArxivarImport: {
    de: "Full Arxivar import",
    it: "Importazione completa Arxivar",
    en: "Full Arxivar import",
  },
  importArxivarUsers: {
    de: "Arixvar Benutzer importieren",
    it: "Importa utenti Arxivar",
    en: "Import Arxivar users",
  },
  importArxivarMandates: {
    de: "Arxivar Mandate importieren",
    it: "Importa mandati Arxivar",
    en: "Import Arxivar mandates",
  },
  importArxivarEmployees: {
    de: "Arxivar Mitarbeiter importieren",
    it: "Importa dipendenti Arxivar",
    en: "Import Arxivar employees",
  },
  importArxivarDocuments: {
    de: "Arxivar Dokumente importieren",
    it: "Importa documenti Arxivar",
    en: "Import Arxivar documents",
  },
  importPspDocuments: {
    de: "PSP Dokumente importieren",
    it: "Importa documenti PSP",
    en: "Import PSP documents",
  },
  updateCalendarEntries: {
    de: "Kalendereinträge aktualisieren",
    it: "Aggiorna voci di calendario",
    en: "Update calendar entries",
  },
  importCircularLetters: {
    de: "Rundschreiben importieren",
    it: "Importa comunicazioni circolari",
    en: "Import circular letters",
  },
  sendEmailWithUploadSummary: {
    de: "Mail mit Upload-Zusammenfassung senden",
    it: "Invia email con riepilogo caricamento",
    en: "Send email with upload summary",
  },
  taskStartedSuccessfully: {
    de: "Task erfolgreich gestartet",
    it: "Task avviata con successo",
    en: "Task started successfully",
  },
  syncRestrictedMandateAccess: {
    de: "Eingeschränkten Mandatszugriff synchronisieren",
    it: "Sincronizza accesso limitato ai mandati",
    en: "Sync restricted mandate access",
  },
  start: {
    de: "Starten",
    it: "Avvia",
    en: "Start",
  },
  rerouteEmailsToPsp: {
    de: "E-Mails auf psp@kivi.bz.it umleiten",
    it: "Reindirizza email a psp@kivi.bz.it",
    en: "Redirect emails to psp@kivi.bz.it",
  },
  yourTokenIsNotFresh: {
    de: "Aus Sicherheitsgründen musst du dich erneut anmelden, um diese Einstellungen zu ändern.",
    // eslint-disable-next-line max-len
    it: "Per motivi di sicurezza devi effettuare nuovamente l'accesso per modificare queste impostazioni.",
    en: "For security reasons, please log in again to change these settings.",
  },
  goToLogin: {
    de: "Zum Login",
    it: "Vai al login",
    en: "Go to login",
  },
  messageEditNotice: {
    // eslint-disable-next-line max-len
    de: "*Das Bearbeiten oder Löschen dieser Nachricht ändert nur den Inhalt in der App. Bereits gesendete E-Mails und Push-Benachrichtigungen bleiben unverändert.",
    // eslint-disable-next-line max-len
    it: "*La modifica o l'eliminazione di questo messaggio aggiorna solo il contenuto nell’app. Le email e le notifiche push già inviate non verranno modificate.",
    // eslint-disable-next-line max-len
    en: "*Editing or deleting this message only updates its content in the app. Emails and push notifications that have already been sent will not be changed.",
  },
  sessionTimedOut: {
    de: "Die Sitzung ist abgelaufen.",
    it: "La sessione è scaduta.",
    en: "Session timed out.",
  },
  theCodeIsIncorrect: {
    de: "Der eingegebene Code ist falsch.",
    it: "Il codice inserito è sbagliato.",
    en: "The submitted code is incorrect.",
  },
  theCodeIsTooShort: {
    de: "Der eingegebene Code ist zu kurz.",
    it: "Il codice inserito è troppo corto.",
    en: "The submitted code is too short.",
  },
  theCodeIsTooLong: {
    de: "Der eingegebene Code ist zu lang.",
    it: "Il codice inserito è troppo lungo.",
    en: "The submitted code is too long.",
  },
  deleteUploadConfirmationMessage: {
    // eslint-disable-next-line max-len
    de: "Du bist dabei, dieses hochgeladene Dokument zu löschen. Dieser Vorgang kann nicht rückgängig gemacht werden. Bist du dir sicher?",
    // eslint-disable-next-line max-len
    it: "Stai per eliminare questo documento caricato. Questa azione non può essere annullata. Sei sicuro di voler procedere?",
    en: "You’re about to delete this uploaded document. This action can’t be undone. Are you sure?",
  },
  documentDeletedSuccessfully: {
    de: "Dokument erfolgreich gelöscht.",
    it: "Documento eliminato con successo.",
    en: "Document deleted successfully.",
  },
  accountSecurity: {
    de: "Kontosicherheit",
    it: "Sicurezza dell'account",
    en: "Account security",
  },
  youAreAboutToResetMfa: {
    // eslint-disable-next-line max-len
    de: "Warnung: Sie sind dabei, alle Multi-Faktor-Authentifizierungsmethoden für dieses Konto zu deaktivieren und zu entfernen. Dadurch wird die Kontosicherheit verringert, bis die MFA erneut eingerichtet wird. Möchten Sie wirklich fortfahren?",
    // eslint-disable-next-line max-len
    it: "Attenzione: stai per disattivare e rimuovere tutti i metodi di autenticazione a più fattori per questo account. Questo ridurrà la sicurezza dell’account fino a quando l’MFA non verrà riconfigurato. Sei sicuro di voler procedere?",
    // eslint-disable-next-line max-len
    en: "Warning: You are about to disable and remove all multi-factor authentication methods for this account. This will reduce account security until MFA is reconfigured. Are you sure you want to proceed?",
  },
  resetMfa: {
    de: "MFA zurücksetzen",
    it: "Ripristina MFA",
    en: "Reset MFA",
  },
  resetMfaSuccess: {
    de: "MFA erfolgreich zurückgesetzt.",
    it: "MFA ripristinata con successo.",
    en: "MFA successfully reset.",
  },
  sender: {
    de: "Sender",
    it: "Mittente",
    en: "Sender",
  },
  noMandateFoundTitle: {
    de: "Kein Mandat gefunden",
    it: "Nessun mandato trovato",
    en: "No mandate found",
  },
  noMandateFoundBody0: {
    de: "In Arxivar prüfen, ob ",
    it: "In Arxivar verificare se ",
    en: "In Arxivar, check whether ",
  },
  noMandateFoundBody1Bold: {
    de: "AltriDa (Kundenkartei)",
    it: "AltriDa (archivio clienti)",
    en: "AltriDa (client file)",
  },
  noMandateFoundBody2: {
    de: " und ",
    it: " e ",
    en: " and ",
  },
  noMandateFoundBody3Bold: {
    de: "Codice (Rubrica)",
    it: "Codice (rubrica)",
    en: "Codice (address book)",
  },
  noMandateFoundBody4: {
    de: " übereinstimmen.",
    it: " corrispondono.",
    en: " match.",
  },
  noUserFoundTitle: {
    de: "Kein Benutzer gefunden",
    it: "Nessun utente trovato",
    en: "No user found",
  },
  noUserFoundBody: {
    // eslint-disable-next-line max-len
    de: "E-Mail in der Rubrica prüfen. Fehlt sie dort, im Mandat unter Benutzer den Hauptnutzer suchen.",
    // eslint-disable-next-line max-len
    it: "Controllare l'e-mail nella Rubrica. Se non è presente, cercare l'utente principale nel mandato sotto Utenti.",
    // eslint-disable-next-line max-len
    en: "Check the email in the address book. If it's missing there, look for the main user under Users in the mandate.",
  },
  noExternalUserFoundTitle: {
    de: "Kein externer Benutzer gefunden",
    it: "Nessun utente esterno trovato",
    en: "No external user found",
  },
  noExternalUserFoundBody: {
    de: "Externe Benutzer werden direkt in der App angelegt.",
    it: "Gli utenti esterni vengono creati direttamente nell'app.",
    en: "External users are created directly in the app.",
  },
  noEmployeesFoundTitle: {
    de: "Kein Mitarbeiter gefunden",
    it: "Nessun dipendente trovato",
    en: "No employees found",
  },
  noEmployeesFoundBody0: {
    de: "Mitarbeiter erscheinen hier, wenn sie in Arxivar folgende Bedingungen erfüllen:",
    it: "I dipendenti appaiono qui se in Arxivar soddisfano le seguenti condizioni:",
    en: "Employees will appear here if in Arxivar they meet the following conditions:",
  },
  noEmployeesFoundBody1a: {
    // eslint-disable-next-line quotes
    de: 'in der Domäne "',
    // eslint-disable-next-line quotes
    it: 'sono registrati nel dominio "',
    // eslint-disable-next-line quotes
    en: 'are registered in the domain "',
  },
  noEmployeesFoundBody1bBold: {
    de: "PSP",
    it: "PSP",
    en: "PSP",
  },
  noEmployeesFoundBody1c: {
    // eslint-disable-next-line quotes
    de: '" eingetragen sind,',
    // eslint-disable-next-line quotes
    it: '"',
    // eslint-disable-next-line quotes
    en: '"',
  },
  noEmployeesFoundBody2a: {
    de: "eine E-Mail mit ",
    it: "hanno un'e-mail con ",
    en: "have an email with ",
  },
  noEmployeesFoundBody2bBold: {
    de: "@psp-bz.it",
    it: "@psp-bz.it",
    en: "@psp-bz.it",
  },
  noEmployeesFoundBody2c: {
    de: " haben",
    it: "",
    en: "",
  },
  noEmployeesFoundBody3a: {
    // eslint-disable-next-line quotes
    de: 'StatoUtente "',
    // eslint-disable-next-line quotes
    it: 'con StatoUtente "',
    // eslint-disable-next-line quotes
    en: 'with UserStatus "',
  },
  noEmployeesFoundBody3bBold: {
    de: "Aktiv",
    it: "Attivo",
    en: "Active",
  },
  noEmployeesFoundBody3c: {
    // eslint-disable-next-line quotes
    de: '" gesetzt haben',
    // eslint-disable-next-line quotes
    it: '" impostato',
    // eslint-disable-next-line quotes
    en: '" set',
  },
  noEmployeesFoundBody4Italic: {
    // eslint-disable-next-line max-len
    de: "Für den Login muss der interne Benutzerschlüssel bei Microsoft korrekt konfiguriert sein. Mitarbeiter können sich nur über SSO anmelden.",
    // eslint-disable-next-line max-len
    it: "Per il login, la chiave utente interna in Microsoft deve essere configurata correttamente. I dipendenti possono accedere solo tramite SSO.",
    // eslint-disable-next-line max-len
    en: "For login, the internal user key must be correctly configured in Microsoft. Employees can only sign in via SSO.",
  },
  companyData: {
    de: "Firmendaten",
    it: "Dati aziendali",
    en: "Company data",
  },
  general: {
    de: "Allgemein",
    it: "Generale",
    en: "General",
  },
  arxivarSettings: {
    de: "Arxivar-Einstellungen",
    it: "Impostazioni Arxivar",
    en: "Arxivar settings",
  },
  arxivarSetting: {
    de: "Arxivar-Einstellung",
    it: "Impostazione Arxivar",
    en: "Arxivar setting",
  },
  valueInArxivar: {
    de: "Wert in Arxivar",
    it: "Valore in Arxivar",
    en: "Value in Arxivar",
  },
  toEditSettingOpenMandateInArxivar: {
    de: "Zum Anpassen einer Einstellung, öffne das Mandat direkt in Arxiwar",
    it: "Per modificare un'impostazione, apri direttamente il mandato in Arxivar",
    en: "To edit a setting, open the mandate directly in Arxivar",
  },
  arxivarChangesAppearAfter30Minutes: {
    de: "Änderungen in Arxivar erscheinen hier nach spätestens 30 Minuten",
    it: "Le modifiche in Arxivar appariranno qui entro 30 minuti al massimo",
    en: "Changes in Arxivar will appear here within 30 minutes at the latest",
  },
  accessAndIdentification: {
    de: "Zugang & Identifikation",
    it: "Accesso e identificazione",
    en: "Access & Identification",
  },
  serviceControl: {
    de: "Dienstleitungs-Steuerung",
    it: "Controllo dei servizi",
    en: "Service Control",
  },
  codesAndTechnicalFlags: {
    de: "Codici & technische Flags",
    it: "Codici e flag tecnici",
    en: "Codes & Technical Flags",
  },
  responsibilities: {
    de: "Zuständigkeiten",
    it: "Responsabilità",
    en: "Responsibilities",
  },
  responsibilitiesDesc: {
    de: "Bestimmt, welche Mitarbeiter dem Kunden in der App als Ansprechpartner angezeigt werden.",
    it: "Determina quali collaboratori vengono mostrati al cliente nell'app come referenti.",
    en: "Determines which employees are shown to the customer in the app as contacts.",
  },
  visibleFoldersInApp: {
    de: "Sichtbare Ordner in der App",
    it: "Cartelle visibili nell'app",
    en: "Visible folders in the app",
  },
  visibleFoldersInAppDesc: {
    // eslint-disable-next-line max-len
    de: "Das Mandat hat Zugriff auf diese Ordner. Einzelne Benutzer können unter Rechte enger eingeschränkt werden.",
    // eslint-disable-next-line max-len
    it: "Il cliente ha accesso a queste cartelle. I singoli utenti possono essere limitati ulteriormente nei diritti.",
    // eslint-disable-next-line max-len
    en: "The client has access to these folders. Individual users can be restricted further under rights.",
  },
  arxivarStatusDesc: {
    de: "Wenn inaktiv, hat der Kunde keinen Zugang zur App.",
    it: "Se inattivo, il cliente non ha accesso all'app.",
    en: "If inactive, the client has no access to the app.",
  },
  arxivarActiveDesc: {
    de: "Muss aktiv sein, damit der Kunde in der App erscheint.",
    it: "Deve essere attivo affinché il cliente appaia nell'app.",
    en: "Must be active for the client to appear in the app.",
  },
  pspDoesAccounting: {
    de: "PSP macht Buchhaltung",
    it: "PSP gestisce la contabilità",
    en: "PSP handles accounting",
  },
  pspDoesAccountingDesc: {
    // eslint-disable-next-line quotes
    de: 'Muss "Ja" sein, sonst sieht der Kunde keine Buchhaltung und keine Erklärungen.',
    // eslint-disable-next-line quotes
    it: 'Deve essere "Sì", altrimenti il cliente non vedrà la contabilità né le dichiarazioni.',
    // eslint-disable-next-line quotes
    en: 'Must be "Yes", otherwise the customer will not see accounting or tax returns.',
  },
  pspDoesPayrollAccounting: {
    de: "PSP macht Lohnbuchhaltung",
    it: "PSP gestisce la contabilità delle paghe",
    en: "PSP handles payroll accounting",
  },
  pspDoesPayrollAccountingDesc: {
    // eslint-disable-next-line quotes
    de: 'Muss "Ja" sein, damit der Ordner Lohnbuchhaltung erscheint.',
    // eslint-disable-next-line quotes
    it: 'Deve essere "Sì", in modo che appaia la cartella contabilità paghe.',
    // eslint-disable-next-line quotes
    en: 'Must be "Yes" for the payroll accounting folder to appear.',
  },
  legalForm: {
    de: "Rechtsform",
    it: "Forma giuridica",
    en: "Legal form",
  },
  legalFormDesc: {
    // eslint-disable-next-line quotes, max-len
    de: 'Bei "private Steuererklärungen" wird kein Buchhaltungs-Ordner angezeigt - nur Erklärungen.',
    // eslint-disable-next-line quotes, max-len
    it: 'Con "private Steuererklärungen" non viene mostrata la cartella della contabilità - solo le dichiarazioni.',
    // eslint-disable-next-line quotes
    en: 'For "private Steuererklärungen", no accounting folder is shown — only declarations.',
  },
  accountingService: {
    de: "Dienstleistung Buchhaltung",
    it: "Servizio di contabilità",
    en: "Accounting service",
  },
  accountingServiceDesc: {
    // eslint-disable-next-line max-len
    de: "Bestimmt welche Steuererklärungen für diesen Kunden gemacht werden. Dient Berichten und Auswertungen.",
    // eslint-disable-next-line max-len
    it: "Determina quali dichiarazioni fiscali vengono fatte per questo cliente. Serve per report e analisi.",
    // eslint-disable-next-line max-len
    en: "Determines which tax returns are prepared for this client. Used for reporting and analytics.",
  },
  codiceMulti: {
    de: "Codice Multi",
    it: "Codice Multi",
    en: "Codice Multi",
  },
  codiceMultiDesc: {
    de: "Codice Ditta Buchhaltung",
    it: "Codice Ditta Buchhaltung",
    en: "Codice Ditta Buchhaltung",
  },
  codicePaghe: {
    de: "Codice Paghe",
    it: "Codice Paghe",
    en: "Codice Paghe",
  },
  codicePagheDesc: {
    de: "Codice Ditta Löhne",
    it: "Codice Ditta Löhne",
    en: "Codice Ditta Löhne",
  },
  anzahlAbrechnungen: {
    de: "Anzahl Abrechnungen",
    it: "Numero di cedolini",
    en: "Number of monthly salaries",
  },
  anzahlAbrechnungenDesc: {
    de: "Anzahl der Lohn-/Gehaltsabrechnungen, die ein Betrieb pro Jahr auszahlt",
    it: "Numero di retribuzioni mensili erogate dall'azienda in un anno",
    en: "Number of salary payments an employer pays out per year",
  },
  tsStudioHR: {
    de: "TS Studio HR",
    it: "TS Studio HR",
    en: "TS Studio HR",
  },
  tsStudioHRDesc: {
    de: "Aktiviert den Studio-HR Zugang für den Kunden.",
    it: "Attiva l'accesso a Studio-HR per il cliente.",
    en: "Activates the Studio-HR access for the client.",
  },
  accountindBranch: {
    de: "Filiale Buchhaltung",
    it: "Filiale contabilità",
    en: "Accounting branch",
  },
  payrollAccountingBranch: {
    de: "Filiale Lohnbuchhaltung",
    it: "Filiale contabilità delle paghe",
    en: "Payroll accounting branch",
  },
  aAccounting: {
    de: "A - Buchhaltung",
    it: "A - Contabilità",
    en: "A - Accounting",
  },
  aAccountingDesc: {
    // eslint-disable-next-line max-len, quotes
    de: 'Sichtbar wenn: "PSP macht Buchhaltung" = "Ja" und "Rechtsform" gesetzt ist (und nicht "private Steuererklärungen" ist).',
    // eslint-disable-next-line max-len, quotes
    it: 'Visibile se: "PSP gestisce la contabilità" = "Sì" e la "Forma giuridica" è impostata (e non è "private Steuererklärungen").',
    // eslint-disable-next-line max-len, quotes
    en: 'Visible if: "PSP handles accounting" = "Yes" and "Legal form" is set (and is not "private Steuererklärungen").',
  },
  cTaxReturn: {
    de: "C - Erklärung",
    it: "C - Dichiarazione",
    en: "C - Tax return",
  },
  cTaxReturnDesc: {
    // eslint-disable-next-line quotes
    de: 'Sichtbar wenn: "PSP macht Buchhaltung" = "Ja".',
    // eslint-disable-next-line quotes
    it: 'Visibile se: "PSP gestisce la contabilità" = "Sì".',
    // eslint-disable-next-line quotes
    en: 'Visible if: "PSP handles accounting" = "Yes".',
  },
  dPayrollAccounting: {
    de: "D - Lohnbuchhaltung",
    it: "D - Contabilità paghe",
    en: "D - Payroll accounting",
  },
  dPayrollAccountingDesc: {
    // eslint-disable-next-line quotes
    de: 'Sichtbar wenn: "PSP macht Lohnbuchhaltung " = "Ja".',
    // eslint-disable-next-line quotes
    it: 'Visibile se: "PSP gestisce la contabilità delle paghe" = "Sì".',
    // eslint-disable-next-line quotes
    en: 'Visible if: "PSP handles payroll accounting" = "Yes".',
  },
  mainUserEmailEditMandateDesc: {
    // eslint-disable-next-line max-len, quotes
    de: 'Die E-Mail des Hauptbenutzers kann in Arxivar bearbeitet werden. Sie befindet sich im Feld "MAIL (DM_DATIPROFILO)".',
    // eslint-disable-next-line max-len
    it: "L'email dell'utente principale può essere modificata in Arxivar. Si trova nel campo \"MAIL (DM_DATIPROFILO)\".",
    // eslint-disable-next-line max-len, quotes
    en: 'The email of the main user can be edited in Arxivar. It can be found under the field "MAIL (DM_DATIPROFILO)".',
  },
  ACCOUNTING_MWST_EINKAUFSREGISTER: {
    de: "MwSt. Einkaufsregister",
    it: "Registro IVA acquisti",
    en: "VAT purchase register",
  },
  ACCOUNTING_MWST_VERKAUFSREGISTER: {
    de: "MwSt. Verkaufsregister",
    it: "Registro IVA vendite",
    en: "VAT sales register",
  },
  ACCOUNTING_MWST_TAGESEINNAHMEN: {
    de: "MwSt. Tageseinnahmen",
    it: "Incassi giornalieri IVA",
    en: "VAT daily receipts",
  },
  ACCOUNTING_MWST_ABRECHNUNG: {
    de: "MwSt. Abrechnung",
    it: "Liquidazione IVA",
    en: "VAT settlement",
  },
  ACCOUNTING_MWST_JAHRESERKLAERUNG: {
    de: "MwSt. Jahreserklärung",
    it: "Dichiarazione IVA annuale",
    en: "Annual VAT return",
  },
  ACCOUNTING_MWST_RUECKERSTATTUNG: {
    de: "MwSt. Rückerstattung",
    it: "Rimborso IVA",
    en: "VAT refund",
  },
  ACCOUNTING_MWST_LIQUIDATION: {
    de: "MwSt. Liquidation",
    it: "Liquidazione IVA",
    en: "VAT liquidation",
  },
  ACCOUNTING_JAHRESBILANZ: {
    de: "Jahresbilanz",
    it: "Bilancio annuale",
    en: "Annual financial statements",
  },
  ACCOUNTING_JAHRESBILANZ_VORJAHRESVERGLEICH: {
    de: "Jahresbilanz Vorjahresvergleich",
    it: "Bilancio annuale con confronto anno precedente",
    en: "Annual financial statements with prior-year comparison",
  },
  ACCOUNTING_ZWISCHENBILANZ: {
    de: "Zwischenbilanz",
    it: "Bilancio intermedio",
    en: "Interim financial statements",
  },
  ACCOUNTING_ZWISCHENBILANZ_VORJAHRESVERGLEICH: {
    de: "Zwischenbilanz Vorjahresvergleich",
    it: "Bilancio intermedio con confronto anno precedente",
    en: "Interim financial statements with prior-year comparison",
  },
  ACCOUNTING_EU_BILANZ: {
    de: "EU-Bilanz",
    it: "Bilancio UE",
    en: "EU financial statements",
  },
  ACCOUNTING_KONTENBLAETTER: {
    de: "Kontenblätter",
    it: "Schede contabili",
    en: "General ledger accounts",
  },
  ACCOUNTING_JOURNAL: {
    de: "Journal",
    it: "Giornale contabile",
    en: "Accounting journal",
  },
  ACCOUNTING_ABSCHREIBEREGISTER: {
    de: "Abschreiberegister",
    it: "Registro dei cespiti ammortizzabili",
    en: "Depreciation register",
  },
  ACCOUNTING_OFFENE_POSTEN_KUNDEN: {
    de: "Offene Posten Kunden",
    it: "Partite aperte clienti",
    en: "Open customer receivables",
  },
  ACCOUNTING_OFFENE_POSTEN_LIEFERANTEN: {
    de: "Offene Posten Lieferanten",
    it: "Partite aperte fornitori",
    en: "Open supplier payables",
  },
  ACCOUNTING_F24: {
    de: "Buchhaltung F24",
    it: "Contabilità F24",
    en: "Accounting F24",
  },
  ACCOUNTING_ABSICHTSERKLAERUNGEN: {
    de: "Absichtserklärungen",
    it: "Dichiarazioni d'intento",
    en: "Declarations of intent",
  },
  ACCOUNTING_INTRASTAT_MELDUNG: {
    de: "Intrastat-Meldung",
    it: "Dichiarazione Intrastat",
    en: "Intrastat report",
  },
  ACCOUNTING_730: {
    de: "Steuererklärung Modell 730",
    it: "Modello 730",
    en: "Form 730 (Italian tax return)",
  },
  ACCOUNTING_UNICO_PF: {
    de: "Steuererklärung Modell UNICO PF",
    it: "Modello Redditi PF",
    en: "UNICO PF tax return",
  },
  ACCOUNTING_UNICO_SP: {
    de: "Steuererklärung Modell UNICO SP",
    it: "Modello Redditi SP",
    en: "UNICO SP tax return",
  },
  ACCOUNTING_UNICO_SC: {
    de: "Steuererklärung Modell UNICO SC",
    it: "Modello Redditi SC",
    en: "UNICO SC tax return",
  },
  ACCOUNTING_UNICO_ENC: {
    de: "Steuererklärung Modell UNICO ENC",
    it: "Modello Redditi ENC",
    en: "UNICO ENC tax return",
  },
  ACCOUNTING_CU: {
    de: "CU (Zertifizierte Lohnbescheinigung)",
    it: "CU (Certificazione Unica)",
    en: "CU (single certification)",
  },
  ACCOUNTING_SONSTIGE: {
    de: "Sonstige",
    it: "Altri",
    en: "Other",
  },
  PAYROLL_LOHNSTREIFEN: {
    de: "Lohnstreifen",
    it: "Busta paga",
    en: "Payslip",
  },
  PAYROLL_BUCHUNGSBELEG: {
    de: "Buchungsbeleg",
    it: "Prospetto contabile",
    en: "Payroll posting document",
  },
  PAYROLL_BUCHUNGSBELEG_PRO_KOSTENSTELLE: {
    de: "Buchungsbeleg pro Kostenstelle",
    it: "Prospetto contabile per centro di costo",
    en: "Posting document per cost center",
  },
  PAYROLL_BUCHUNGSBELEG_PRO_MITARBEITER: {
    de: "Buchungsbeleg pro Mitarbeiter",
    it: "Prospetto contabile per dipendente",
    en: "Posting document per employee",
  },
  PAYROLL_NETTOLISTE: {
    de: "Nettoliste",
    it: "Lista netti",
    en: "Net pay list",
  },
  PAYROLL_URLAUBSUEBERSICHT: {
    de: "Urlaubsübersicht",
    it: "Riepilogo ferie",
    en: "Vacation overview",
  },
  PAYROLL_PFAENDUNGEN_FINANZIERUNGEN: {
    de: "Pfändungen / Finanzierungen",
    it: "Pignoramenti / finanziamenti",
    en: "Wage garnishments / loans",
  },
  PAYROLL_GEWERKSCHAFTSBEITRAEGE: {
    de: "Gewerkschaftsbeiträge",
    it: "Contributi sindacali",
    en: "Union contributions",
  },
  PAYROLL_BLUTSPENDE: {
    de: "Blutspende",
    it: "Donazione sangue",
    en: "Blood donation",
  },
  PAYROLL_F24: {
    de: "Lohnbuchhaltung F24",
    it: "Contabilizzazione paghe F24",
    en: "Payroll accounting F24",
  },
  PAYROLL_EINZAHLUNGSSCHEIN_SANIFONDS: {
    de: "Einzahlungsschein Sanifonds",
    it: "Bollettino SANIFONDS",
    en: "SANIFONDS payment slip",
  },
  PAYROLL_ABFERTIGUNGSFONDS: {
    de: "Abfertigungsfonds",
    it: "Fondo TFR",
    en: "Severance fund (TFR)",
  },
  PAYROLL_RUECKSTELLUNGEN: {
    de: "Rückstellungen",
    it: "Accantonamenti",
    en: "Provisions",
  },
  PAYROLL_UEBERWEISUNGSFILE_LOEHNE: {
    de: "Überweisungsdatei Löhne",
    it: "File bonifici stipendi",
    en: "Payroll bank transfer file",
  },
  PAYROLL_EINZAHLUNGSSCHEIN_BAUARBEITERKASSE: {
    de: "Einzahlungsschein Bauarbeiterkasse",
    it: "Cassa edile",
    en: "Construction workers fund payment slip",
  },
  PAYROLL_EINZAHLUNGSSCHEIN_CASAGIT: {
    de: "Einzahlungsschein CASAGIT",
    it: "CASAGIT",
    en: "CASAGIT payment slip",
  },
  PAYROLL_UEBERWEISUNGSFILE_ZUSATZRENTENFONDS: {
    de: "Überweisungsdatei Zusatzrentenfonds",
    it: "File bonifici fondo pensione complementare",
    en: "Supplementary pension fund transfer file",
  },
  PAYROLL_EFFEKTIVE_LOHNKOSTEN: {
    de: "Effektive Lohnkosten",
    it: "Costo effettivo del lavoro",
    en: "Effective labor cost",
  },
  PAYROLL_ZAHLUNGSBESTAETIGUNG_F24: {
    de: "Zahlungsbestätigung F24",
    it: "Ricevuta pagamento F24",
    en: "F24 payment confirmation",
  },
  PAYROLL_SONSTIGE: {
    de: "Sonstige",
    it: "Altri",
    en: "Other",
  },
  folder: {
    de: "Ordner",
    it: "Cartella",
    en: "Folder",
  },
  employmentLawData: {
    de: "Arbeitsrechtliche Daten",
    it: "Dati di diritto del lavoro",
    en: "Employment law data",
  },
  inpsRegistrationNumber: {
    de: "Matrikelnummer INPS",
    it: "Numero di matricola INPS",
    en: "INPS registration number",
  },
  nationalCollectiveLabourAgreements: {
    de: "Kollektivverträge",
    it: "Contratti collettivi nazionali del lavoro (CCNL)",
    en: "National collective labour agreements",
  },
  nationalCollectiveLabourAgreementsArxivarDesc: {
    // eslint-disable-next-line max-len
    de: "Wenn das Feld für die nationalen Kollektivverträge die entsprechenden Codes nicht enthält, funktioniert die Anwesenheitsfunktion nicht korrekt.",
    // eslint-disable-next-line max-len
    it: "Se il campo dei contratti collettivi nazionali non contiene i codici pertinenti, la funzionalità delle presenze non funzionerà correttamente.",
    // eslint-disable-next-line max-len
    en: "If the national collective labour agreements field does not contain the relevant codes, the attendances functionality will fail to work correctly.",
  },
  nationalCollectiveLabourAgreementCodes: {
    de: "Codici Kollektivverträge",
    it: "Codici contratti collettivi nazionali del lavoro (CCNL)",
    en: "Codici national collective labour agreements",
  },
  derivedFromNationalCollectiveLabourAgreements: {
    de: "Von Kollektivverträge abgeleitet",
    it: "Derivati dai contratti collettivi nazionali del lavoro (CCNL)",
    en: "Derived from national collective labour agreements",
  },
  inailRegistrationNumber: {
    de: "Matrikelnummer INAIL",
    it: "Numero di matricola INAIL",
    en: "INAIL registration number",
  },
  addDeadline: {
    de: "Fälligkeit hinzufügen",
    it: "Aggiungi scadenza",
    en: "Add deadline",
  },
  deadline: {
    de: "Fälligkeit",
    it: "Scadenza",
    en: "Deadline",
  },
  pspDocumentDeadlineNotice: {
    de: "Der eingegebene Text ist für den Kunden in seinem Kalender sichtbar.",
    it: "Il testo inserito è visibile al cliente nel suo calendario.",
    en: "The entered text is visible to the customer in their calendar.",
  },
  month0: {
    de: "Januar",
    it: "Gennaio",
    en: "January",
  },
  month1: {
    de: "Februar",
    it: "Febbraio",
    en: "February",
  },
  month2: {
    de: "März",
    it: "Marzo",
    en: "March",
  },
  month3: {
    de: "April",
    it: "Aprile",
    en: "April",
  },
  month4: {
    de: "Mai",
    it: "Maggio",
    en: "May",
  },
  month5: {
    de: "Juni",
    it: "Giugno",
    en: "June",
  },
  month6: {
    de: "Juli",
    it: "Luglio",
    en: "July",
  },
  month7: {
    de: "August",
    it: "Agosto",
    en: "August",
  },
  month8: {
    de: "September",
    it: "Settembre",
    en: "September",
  },
  month9: {
    de: "Oktober",
    it: "Ottobre",
    en: "October",
  },
  month10: {
    de: "November",
    it: "Novembre",
    en: "November",
  },
  month11: {
    de: "Dezember",
    it: "Dicembre",
    en: "December",
  },
  month12: {
    de: "13er",
    it: "13esima",
    en: "13th",
  },
  month13: {
    de: "14er",
    it: "14esima",
    en: "14th",
  },
  month14: {
    de: "15er",
    it: "15esima",
    en: "15th",
  },
  noMonthSelected: {
    de: "Kein Monat ausgewählt",
    it: "Nessun mese selezionato",
    en: "No month selected",
  },
  selectAMandateFirst: {
    de: "Wähle zuerst ein Mandat aus",
    it: "Seleziona prima un mandato",
    en: "Select a mandate first",
  },
  attendances: {
    de: "Präsenzen",
    it: "Presenze",
    en: "Attendances",
  },
  wrongResetCode: {
    de: "Der Verifizierungscode ist falsch.",
    it: "Il codice di verifica è errato.",
    en: "The verification code is wrong.",
  },
  sum: {
    de: "Summe",
    it: "Somma",
    en: "Sum",
  },
  submitAttendance: {
    de: "Präsenzen absenden",
    it: "Invia le presenze",
    en: "Submit attendance",
  },
  restoreOriginal: {
    de: "Original wiederherstellen",
    it: "Ripristina l'originale",
    en: "Restore original",
  },
  restore: {
    de: "Wiederherstellen",
    it: "Ripristina",
    en: "Restore",
  },
  originalRestoredMobile: {
    de: "Die Originalversion wurde wiederhergestellt.",
    it: "La versione originale è stata ripristinata.",
    en: "The original version has been restored.",
  },
  versionRestoredSuccessfully: {
    de: "Version vom {date} erfolgreich wiederhergestellt.",
    it: "Versione del {date} ripristinata con successo.",
    en: "Version from {date} restored successfully.",
  },
  originalRestoredDesktop: {
    // eslint-disable-next-line max-len, quotes
    de: 'Die Originalversion wurde wiederhergestellt. Klicken Sie auf "Speichern", um Ihre Änderungen zu behalten.',
    // eslint-disable-next-line quotes
    it: 'La versione originale è stata ripristinata. Premi "Salva" per conservare le modifiche.',
    // eslint-disable-next-line quotes
    en: 'The original version has been restored. Press "save" to preserve your changes.',
  },
  changesSavedSuccessfully: {
    de: "Die Änderungen wurden erfolgreich gespeichert",
    it: "Modifiche salvate con successo",
    en: "Changes saved successfully",
  },
  youHaveUnsavedChanges: {
    // eslint-disable-next-line max-len
    de: "Du hast ungespeicherte Änderungen, bist du sicher, dass du fortfahren möchtest? Die Änderungen werden verloren gehen.",
    it: "Hai modifiche non salvate, sei sicuro di voler continuare? Le modifiche andranno perse",
    en: "You have unsaved changes, Are you sure you want to continue? The changes will be lost.",
  },
  templateLanguage: {
    de: "Sprache der Vorlage",
    it: "Lingua del modello",
    en: "Template language",
  },
  youAreAboutToRestoreOriginalAttendance: {
    de: "Alle deine Änderungen werden dabei entfernt und können nicht wiederhergestellt werden.",
    it: "Tutte le modifiche verranno rimosse e non potranno essere recuperate.",
    en: "All your changes will be removed and cannot be recovered.",
  },
  generic: {
    de: "Allgemein",
    it: "Generico",
    en: "Generic",
  },
  twoFa: {
    de: "2-FA",
    it: "2-FA",
    en: "2-FA",
  },
  allMandates: {
    de: "Alle Mandate",
    it: "Tutti i mandati",
    en: "All mandates",
  },
  resetMfaButtonDescription: {
    // eslint-disable-next-line max-len
    de: "Setzt alle 2FA-Methoden des Benutzers zurück. Der Benutzer muss die Zwei-Faktor-Authentifizierung beim nächsten Login neu einrichten.",
    // eslint-disable-next-line max-len
    it: "Reimposta tutti i metodi di autenticazione a due fattori (2FA) dell'utente. L'utente dovrà configurare nuovamente l'autenticazione a due fattori al successivo accesso.",
    // eslint-disable-next-line max-len
    en: "Resets all of the user's two-factor authentication (2FA) methods. The user will be required to set up two-factor authentication again the next time they sign in.",
  },
  security: {
    de: "Sicherheit",
    it: "Sicurezza",
    en: "Security",
  },
  password_too_short: {
    de: "Mindestens 9 Zeichen",
    it: "Almeno 9 caratteri",
    en: "At least 9 characters",
  },
  password_too_long: {
    de: "Zu viele Zeichen",
    it: "Troppi caratteri",
    en: "Too many characters",
  },
  password_missing_letter: {
    de: "Mindestens ein Buchstabe",
    it: "Almeno una lettera",
    en: "At least one letter",
  },
  password_missing_uppercase_letter: {
    de: "Mindestens ein Großbuchstabe",
    it: "Almeno una lettera maiuscola",
    en: "At least one uppercase letter",
  },
  password_missing_lowercase_letter: {
    de: "Mindestens ein Kleinbuchstabe",
    it: "Almeno una lettera minuscola",
    en: "At least one lowercase letter",
  },
  password_missing_digit: {
    de: "Mindestens eine Zahl",
    it: "Almeno un numero",
    en: "At least one number",
  },
  password_missing_symbol: {
    de: "Mindestens ein Sonderzeichen (!@#$%^&*)",
    it: "Almeno un carattere speciale (!@#$%^&*)",
    en: "At least one special character (!@#$%^&*)",
  },
  nonAdminsCannotEditAdminsMessage: {
    de: "Nicht-Admins können Admin-Benutzer nicht bearbeiten.",
    it: "I non-amministratori non possono modificare utenti amministratori.",
    en: "Non-admins cannot edit admin users.",
  },
  impersonateUser: {
    de: "Als Benutzer anmelden",
    it: "Accedi come utente",
    en: "Impersonate user",
  },
  impersonateUserButtonDescription: {
    de: "Meldet dich als dieser Benutzer an, damit du die App aus seiner Sicht sehen kannst.",
    it: "Accedi come questo utente per vedere l'app dal suo punto di vista.",
    en: "Signs you in as this user so you can see the app from their point of view.",
  },
  viewingAsUser: {
    de: "Angemeldet als {userName}",
    it: "Accesso effettuato come {userName}",
    en: "Viewing as {userName}",
  },
  returnToAdmin: {
    de: "Zurück zum Admin-Konto",
    it: "Torna all'account amministratore",
    en: "Return to admin",
  },
  notificationRemovedSuccessfully: {
    de: "Benachrichtigung erfolgreich entfernt.",
    it: "Notifica rimossa con successo.",
    en: "Notification removed successfully.",
  },
  addNoteForName: {
    de: "Notiz für {name} hinzufügen",
    it: "Aggiungi nota per {name}",
    en: "Add note for {name}",
  },
  voci: {
    de: "Voci",
    it: "Voci",
    en: "Voci",
  },
  enterHours: {
    de: "Stunden eintragen",
    it: "Inserimento ore",
    en: "Enter hours",
  },
  regularHours: {
    de: "Reguläre Stunden",
    it: "Ore regolari",
    en: "Regular hours",
  },
  addGiustificativo: {
    de: "Giustificativo hinzufügen",
    it: "Aggiungi giustificativo",
    en: "Add giustificativo",
  },
  submitAttendanceConfirmationWarning: {
    // eslint-disable-next-line max-len
    de: "Sobald du die Anwesenheiten abgesendet hast, kann sie nicht mehr widerrufen oder geändert werden. Bist du sicher?",
    it: "Una volta inviate le presenze, non potranno più essere revocate o modificate. Sei sicuro?",
    en: "Once you have submitted the attendance, it cannot be revoked or edited. Are you sure?",
  },
  versionHistory: {
    de: "Versionsverlauf",
    it: "Cronologia delle versioni",
    en: "Version history",
  },
  attendanceNoteMessage: {
    de: "Die Notiz ist für das Lohnbüro sichtbar und gilt für {employeeName} im {month} {year}",
    it: "La nota è visibile all'ufficio paghe e si applica a {employeeName} per {month} {year}",
    en: "The note is visible to payroll and applies to {employeeName} for {month} {year}",
  },
  noteSavedSuccessfully: {
    de: "Notiz erfolgreich gespeichert",
    it: "Nota salvata con successo",
    en: "Note saved successfully",
  },
  cantRemovePrimaryUser: {
    de: "Hauptnutzer und Berater können nicht aus einem Mandat entfernt werden.",
    it: "Gli utenti principali e i consulenti non possono essere rimossi da un mandato.",
    en: "Primary users and advisors cannot be removed from a mandate.",
  },
  youAreAboutToRestoreVersion: {
    de: "Sie sind dabei, die Version vom {date} wiederherzustellen. Bist du sicher?",
    it: "Stai per ripristinare la versione del {date}. Sei sicuro?",
    en: "You are about to restore the version from {date}. Are you sure?",
  },
  draft: {
    de: "Entwurf",
    it: "Bozza",
    en: "Draft",
  },
  saveDraft: {
    de: "Entwurf speichern",
    it: "Salva bozza",
    en: "Salva draft",
  },
  base: {
    de: "Basis",
    it: "Base",
    en: "Base",
  },
  amountIsNotAValidNumber: {
    // eslint-disable-next-line quotes
    de: '"Betrag" ist nicht eine gültige Zahl',
    // eslint-disable-next-line quotes
    it: '"Totale" non è un numero valido',
    // eslint-disable-next-line quotes
    en: '"Amount" is not a valid number',
  },
  quantityIsNotAValidNumber: {
    // eslint-disable-next-line quotes
    de: '"Menge" ist nicht eine gültige Zahl',
    // eslint-disable-next-line quotes
    it: '"Quantità" non è un numero valido',
    // eslint-disable-next-line quotes
    en: '"Quantity" is not a valid number',
  },
  baseIsNotAValidNumber: {
    // eslint-disable-next-line quotes
    de: '"Basis" ist nicht eine gültige Zahl',
    // eslint-disable-next-line quotes
    it: '"Base" non è un numero valido',
    // eslint-disable-next-line quotes
    en: '"Base" is not a valid number',
  },
  amount: {
    de: "Betrag",
    it: "Totale",
    en: "Amount",
  },
  quantity: {
    de: "Menge",
    it: "Quantità",
    en: "Quantity",
  },
  enteredVoci: {
    de: "Eingetragene Voci",
    it: "Voci inserite",
    en: "Entered Voci",
  },
  youAreAboutToRemoveVoce: {
    // eslint-disable-next-line quotes
    de: 'Du bist dabei, die Voce "{voceName}" von "{employeeName}" zu entfernen. Bist du sicher?',
    // eslint-disable-next-line quotes
    it: 'Stai per rimuovere la voce "{voceName}" da "{employeeName}". Sei sicuro?',
    // eslint-disable-next-line quotes
    en: 'You are about to remove the voce "{voceName}" from "{employeeName}". Are you sure?',
  },
  couldntSaveChanges: {
    de: "Änderungen konnten nicht gespeichert werden",
    it: "Impossibile salvare le modifiche",
    en: "Couldn't save changes",
  },
  voceAddedSuccessfully: {
    de: "Voce erfolgreich hinzugefügt",
    it: "Voce aggiunta con successo",
    en: "Voce added successfully",
  },
  voceRemovedSuccessfully: {
    de: "Voce erfolgreich entfernt",
    it: "Voce rimossa con successo",
    en: "Voce removed successfully",
  },
  weekdayShort0: {
    de: "Mo.",
    it: "Lu.",
    en: "Mo.",
  },
  weekdayShort1: {
    de: "Di.",
    it: "Ma.",
    en: "Tu.",
  },
  weekdayShort2: {
    de: "Mi.",
    it: "Me.",
    en: "We.",
  },
  weekdayShort3: {
    de: "Do.",
    it: "Gi.",
    en: "Th.",
  },
  weekdayShort4: {
    de: "Fr.",
    it: "Ve.",
    en: "Fr.",
  },
  weekdayShort5: {
    de: "Sa.",
    it: "Sa.",
    en: "Sa.",
  },
  weekdayShort6: {
    de: "So.",
    it: "Do.",
    en: "Su.",
  },
  employeeRestoredSuccessfully: {
    de: "Anwesenheit erfolgreich zurückgesetzt.",
    it: "Presenza ripristinata con successo.",
    en: "Attendance restored succesfully.",
  },
  warningExclam: {
    de: "Achtung!",
    it: "Attenzione!",
    en: "Warning!",
  },
  showMore: {
    de: "Mehr anzeigen",
    it: "Mostra di più",
    en: "Show more",
  },
  hours: {
    de: "Stunden",
    it: "Ore",
    en: "Hours",
  },
  addVoce: {
    de: "Voce hinzufügen",
    it: "Aggiungi voce",
    en: "Add voce",
  },
  searchVoce: {
    de: "Voce suchen",
    it: "Cerca voce",
    en: "Search voce",
  },
  searchGiustificativo: {
    de: "Giustificativo suchen",
    it: "Cerca giustificativo",
    en: "Search giustificativo",
  },
  discardAllChangesAttendancesTitle: {
    de: "Alle Änderungen verwerfen?",
    it: "Annullare tutte le modifiche?",
    en: "Discard all changes?",
  },
  discardAllChangesAttendancesBody: {
    // eslint-disable-next-line max-len
    de: "Alle Änderungen an der Anwesenheit von {employeeName} werden verworfen und der ursprüngliche Stand wiederhergestellt.",
    // eslint-disable-next-line max-len
    it: "Tutte le modifiche alla presenza di {employeeName} verranno annullate e verrà ripristinato lo stato originale.",
    // eslint-disable-next-line max-len
    en: "All changes to {employeeName}'s attendance will be discarded and the original state will be restored.",
  },
  immediateEmail: {
    de: "Sofortige E-Mail",
    it: "E-mail immediata",
    en: "Immediate email",
  },
  immediateEmailDetails: {
    de: "Für jedes hochgeladene Dokument sofort eine E-Mail mit dem Link zum Dokument senden.",
    it: "Inviare immediatamente un'e-mail con il link al documento per ogni documento caricato.",
    en: "Send an email immediately with a link to the document for each uploaded document.",
  },
  hourlyEmail: {
    de: "Stündliche E-Mail",
    it: "E-mail oraria",
    en: "Hourly email",
  },
  hourlyEmailDetails: {
    // eslint-disable-next-line max-len
    de: "Einmal pro Stunde eine Sammel-E-Mail mit allen neuen Dokumenten senden – nur, wenn in der letzten Stunde etwas hochgeladen wurde.",
    // eslint-disable-next-line max-len
    it: "Inviare una volta all'ora un'e-mail riepilogativa con tutti i nuovi documenti, solo se è stato caricato qualcosa nell'ultima ora.",
    // eslint-disable-next-line max-len
    en: "Send a summary email once per hour with all new documents, only if something was uploaded during the last hour.",
  },
  dailyEmail: {
    de: "Tägliche E-Mail",
    it: "E-mail giornaliera",
    en: "Daily email",
  },
  dailyEmailDetails: {
    // eslint-disable-next-line max-len
    de: "Jeden Morgen eine Sammel-E-Mail mit den Dokumenten des Vortags senden – nur, wenn Dokumente hochgeladen wurden.",
    // eslint-disable-next-line max-len
    it: "Inviare ogni mattina un'e-mail riepilogativa con i documenti del giorno precedente, solo se sono stati caricati dei documenti.",
    // eslint-disable-next-line max-len
    en: "Send a summary email every morning with the documents from the previous day, only if documents were uploaded.",
  },
  pushDetails: {
    de: "Wird immer sofort versendet, sobald ein Dokument hochgeladen wird.",
    it: "Viene sempre inviato immediatamente non appena viene caricato un documento.",
    en: "It is always sent immediately as soon as a document is uploaded.",
  },
  noAttendanceAvailable: {
    de: "Es sind keine Anwesenheiten verfügbar.",
    it: "Nessuna presenza disponibile.",
    en: "No attendance available.",
  },
  pleaseSelectMandateShort: {
    de: "Bitte wählen Sie ein Mandat aus",
    it: "Per favore seleziona un mandato",
    en: "Please select a mandate",
  },
  userHasAccessToTheseMandates: {
    de: "Der Benutzer hat auf diese Mandate Zugriff.",
    it: "L'utente ha accesso a questi mandati.",
    en: "The user has access to these mandates.",
  },
  uploadFiles: {
    de: "Dateien hochladen",
    it: "Carica file",
    en: "Upload files",
  },
  forLowercase: {
    de: "für",
    it: "per",
    en: "for",
  },
  uploadFor: {
    de: "Hochladen für",
    it: "Carica per",
    en: "Upload for",
  },
  willBeSentTo: {
    de: "Wird gesendet an",
    it: "Verrà inviato a",
    en: "Will be sent to",
  },
  nameFilesClearly: {
    de: "Bitte Dokumente klar und nachvollziehbar benennen",
    it: "Si prega di denominare i documenti in modo chiaro e comprensibile",
    en: "Please name documents clearly and understandably",
  },
  appSections: {
    de: "App-Bereiche",
    it: "Sezioni app",
    en: "App sections",
  },
  appSectionsDesc: {
    de: "Wähle aus, welche Bereiche Nutzer mit diesem Recht in der App sehen.",
    it: "Seleziona le sezioni che gli utenti con questo diritto possono visualizzare.",
    en: "Select which sections users with this right can view.",
  },
  homeIsVisible: {
    // eslint-disable-next-line quotes
    de: 'Sektion "Home" ist in der App sichtbar',
    // eslint-disable-next-line quotes
    it: 'La sezione "Home" è visibile nell\'app',
    // eslint-disable-next-line quotes
    en: 'The "Home" section is visible in the app',
  },
  pspDocumentsIsVisible: {
    // eslint-disable-next-line quotes
    de: 'Sektion "PSP Dokumente" ist in der App sichtbar',
    // eslint-disable-next-line quotes
    it: 'La sezione "Documenti PSP" è visibile nell\'app',
    // eslint-disable-next-line quotes
    en: 'The "PSP Documents" section is visible in the app',
  },
  submittedDocumentsIsVisible: {
    // eslint-disable-next-line quotes
    de: 'Sektion "Eingereichte Dokumente" ist in der App sichtbar',
    // eslint-disable-next-line quotes
    it: 'La sezione "Documenti inviati" è visibile nell\'app',
    // eslint-disable-next-line quotes
    en: 'The "Submitted documents" section is visible in the app',
  },
  uploadIsVisible: {
    // eslint-disable-next-line quotes
    de: 'Sektion "Hochladen" ist in der App sichtbar',
    // eslint-disable-next-line quotes
    it: 'La sezione "Carica" è visibile nell\'app',
    // eslint-disable-next-line quotes
    en: 'The "Upload" section is visible in the app',
  },
  messagesIsVisible: {
    // eslint-disable-next-line quotes
    de: 'Sektion "Nachrichten" ist in der App sichtbar',
    // eslint-disable-next-line quotes
    it: 'La sezione "Messaggi" è visibile nell\'app',
    // eslint-disable-next-line quotes
    en: 'The "Messages" section is visible in the app',
  },
  attendancesIsVisible: {
    // eslint-disable-next-line quotes
    de: 'Sektion "Präsenzen" ist in der App sichtbar',
    // eslint-disable-next-line quotes
    it: 'La sezione "Presenze" è visibile nell\'app',
    // eslint-disable-next-line quotes
    en: 'The "Attendances" section is visible in the app',
  },
  templatesAndInstructionsIsVisible: {
    // eslint-disable-next-line quotes
    de: 'Sektion "Vorlagen & Anleitungen" ist in der App sichtbar',
    // eslint-disable-next-line quotes
    it: 'La sezione "Modelli e istruzioni" è visibile nell\'app',
    // eslint-disable-next-line quotes
    en: 'The "Templates & instructions" section is visible in the app',
  },
  payrollsApp: {
    de: "App Löhne",
    it: "App Paghe",
    en: "Payrolls app",
  },
  payrollsAppDesc: {
    // eslint-disable-next-line quotes
    de: 'Anwesenheiten Sichtbar wenn "App Löhne" = "PSP App".',
    // eslint-disable-next-line quotes
    it: 'Presenze visibili se "App Paghe" = "PSP App".',
    // eslint-disable-next-line quotes
    en: 'Attendances visible if "Payrolls app" = "PSP App".',
  },
  alreadySubmittedOn: {
    de: "Bereits abgesendet am",
    it: "Già inviate il",
    en: "Already sent on",
  },
  revokeAttendance: {
    de: "Anwesenheiten zurückziehen",
    it: "Ritira presenze",
    en: "Revoke attendance",
  },
  giustificativoDoesNotExist: {
    de: "Diesen Giustificativo gibt es nicht.",
    it: "Questo giustificativo non esiste.",
    en: "This giustificativo does not exist.",
  },
  youAreAboutToRemoveGiustificativo: {
    // eslint-disable-next-line max-len, quotes
    de: 'Du bist dabei, den Giustificativo "{giustificativo}" von "{employeeName}" zu entfernen. Bist du sicher?',
    // eslint-disable-next-line quotes
    it: 'Stai per rimuovere il giustificativo "{giustificativo}" da "{employeeName}". Sei sicuro?',
    // eslint-disable-next-line max-len, quotes
    en: 'You are about to remove the giustificativo "{giustificativo}" from "{employeeName}". Are you sure?',
  },
  actualHoursCannotBeLessThanTheoreticalHours: {
    de: "Effektive Stunden können nicht kleiner als die theoretischen Stunden sein!",
    it: "Ore effettive non possono essere minori delle ore teoriche!",
    en: "Actual hours cannot be less than theoretical hours!",
  },
  yourPasswordIsExpiredMsg: {
    de: "Dein Passwort ist abgelaufen. Lege ein neues fest, um fortzufahren.",
    it: "La tua password è scaduta. Impostane una nuova per continuare.",
    en: "Your password has expired. Set a new one to continue.",
  },
  adminActions: {
    de: "Admin Aktionen",
    it: "Azioni admin",
    en: "Admin actions",
  },
  expireYourOwnPassword: {
    de: "Lass dein eigenes Passwort ablaufen",
    it: "Lascia scadere la tua password",
    en: "Let your own password expire",
  },
  passwordChangeReasonChangeRequired: {
    de: "Änderung erforderlich",
    it: "Modifica richiesta",
    en: "Change required",
  },
  expired: {
    de: "Abgelaufen",
    it: "Scaduta",
    en: "Expired",
  },
  passwordChangeReasonExpired: {
    de: "Abgelaufen",
    it: "Scaduta",
    en: "Expired",
  },
  reason: {
    de: "Grund",
    it: "Motivo",
    en: "Reason",
  },
  cannotReuseOneOfLast5Passwords: {
    de: "Du kannst nicht eines deiner letzten 5 Passwörter verwenden.",
    it: "Non puoi usare una delle tue ultime 5 password.",
    en: "You can't use one of your last 5 passwords.",
  },
  passwordExpiresAt: {
    de: "Passwort läuft ab am",
    it: "Password scade il",
    en: "Password expires at",
  },
  popUps: {
    de: "Pop-ups",
    it: "Pop-up",
    en: "Pop-ups",
  },
  addPopUp: {
    de: "Pop-up erstellen",
    it: "Aggiungi pop-up",
    en: "Add pop-up",
  },
  ended: {
    de: "Beendet",
    it: "Terminato",
    en: "Ended",
  },
  planned: {
    de: "Geplant",
    it: "Pianificato",
    en: "Planned",
  },
  activeUntil: {
    de: "Aktiv bis",
    it: "Attivo fino al",
    en: "Active until",
  },
  content: {
    de: "Inhalt",
    it: "Contenuto",
    en: "Content",
  },
  image: {
    de: "Bild",
    it: "Immagine",
    en: "Image",
  },
  optional: {
    de: "optional",
    it: "facoltativo",
    en: "optional",
  },
  livePreview: {
    de: "Live-Vorschau",
    it: "Anteprima in tempo reale",
    en: "Live preview",
  },
  rules: {
    de: "Regeln",
    it: "Regole",
    en: "Rules",
  },
  internalName: {
    de: "Interner Name",
    it: "Nome interno",
    en: "Internal name",
  },
  headline: {
    de: "Headline",
    it: "Headline",
    en: "Headline",
  },
  text: {
    de: "Text",
    it: "Testo",
    en: "Text",
  },
  buttonText: {
    de: "Button Text",
    it: "Testo pulsante",
    en: "Button text",
  },
  buttonUrl: {
    de: "Button URL",
    it: "URL pulsante",
    en: "Button URL",
  },
  oneImageForAllLanguages: {
    de: "Ein Bild für alle Sprachen",
    it: "Un'immagine per tutte le lingue",
    en: "One image for all languages",
  },
  languageSpecific: {
    de: "Sprachenspezifisch",
    it: "Specifica per ogni lingua",
    en: "Language specific",
  },
  campaign: {
    de: "Kampagne",
    it: "Campagna",
    en: "Campaign",
  },
  reminder: {
    de: "Erinnerung",
    it: "Promemoria",
    en: "Reminder",
  },
  startDate: {
    de: "Startdatum",
    it: "Data d'inizio",
    en: "Start date",
  },
  endDate: {
    de: "Enddatum",
    it: "Data di fine",
    en: "End date",
  },
  once: {
    de: "Einmalig",
    it: "Una volta",
    en: "Once",
  },
  always: {
    de: "Immer",
    it: "Sempre",
    en: "Always",
  },
  daily: {
    de: "Täglich",
    it: "Giornalmente",
    en: "Daily",
  },
  recurrence: {
    de: "Wiederholung",
    it: "Ricorrenza",
    en: "Recurrence",
  },
  weekly: {
    de: "Wöchentlich",
    it: "Settimanalmente",
    en: "Weekly",
  },
  monthly: {
    de: "Monatlich",
    it: "Mensilmente",
    en: "Monthly",
  },
  yearly: {
    de: "Jährlich",
    it: "Annualmente",
    en: "Yearly",
  },
  every: {
    de: "Alle",
    it: "Ogni",
    en: "Every",
  },
  onWeekday: {
    de: "Am",
    it: "Il",
    en: "On",
  },
  documents: {
    de: "Dokumente",
    it: "Documenti",
    en: "Documents",
  },
  submitted: {
    de: "Eingereicht",
    it: "Inviati",
    en: "Submitted",
  },
  psp: {
    de: "PSP",
    it: "PSP",
    en: "PSP",
  },
  monday: {
    de: "Montag",
    it: "Lunedì",
    en: "Monday",
  },
  tuesday: {
    de: "Dienstag",
    it: "Martedì",
    en: "Tuesday",
  },
  wednesday: {
    de: "Mittwoch",
    it: "Mercoledì",
    en: "Wednesday",
  },
  thursday: {
    de: "Donnerstag",
    it: "Giovedì",
    en: "Thursday",
  },
  friday: {
    de: "Freitag",
    it: "Venerdì",
    en: "Friday",
  },
  saturday: {
    de: "Samstag",
    it: "Sabato",
    en: "Saturday",
  },
  sunday: {
    de: "Sonntag",
    it: "Domenica",
    en: "Sunday",
  },
  informRecipientsViaEmail: {
    de: "Empfänger per E-Mail informieren",
    it: "Informa destinatari via e-mail",
    en: "Inform recipients via email",
  },
  tag: {
    de: "Tag",
    it: "Tag",
    en: "Tag",
  },
  templates: {
    de: "Vorlagen",
    it: "Modelli",
    en: "Templates",
  },
  overview: {
    de: "Übersicht",
    it: "Riepilogo",
    en: "Overview",
  },
  createTemplate: {
    de: "Vorlage erstellen",
    it: "Crea modello",
    en: "Create template",
  },
  category: {
    de: "Kategorie",
    it: "Categoria",
    en: "Category",
  },
  numberOfTags: {
    de: "Anzahl an Tags",
    it: "Quantità di tag",
    en: "Number of tags",
  },
  fileTooLarge: {
    de: "Die Datei ist zu groß.",
    it: "Il file è troppo grande.",
    en: "The file is too large.",
  },
  noPreviewAvailable: {
    de: "Keine Vorschau verfügbar.",
    it: "Nessuna anteprima disponibile.",
    en: "No preview available.",
  },
  allFilesTooBig: {
    de: "Zu viele Dateien ausgewählt, es können nur maximal [bytes] hochgeladen werden",
    it: "Sono stati selezionati troppi file; è possibile caricare al massimo [bytes]",
    en: "Too many files selected; a maximum of [bytes] can be uploaded.",
  },
  fileTooLargeInfo: {
    de: "Die Datei ist zu groß, maximal [bytes].",
    it: "Il file è troppo grande, massimo [bytes].",
    en: "The file is too large; the maximum size is [bytes].",
  },
  siteNotFound: {
    de: "Seite nicht gefunden",
    it: "Pagina non trovata",
    en: "Page not found",
  },
  siteNotFoundInfo: {
    de: "Die gesuchte Seite existiert nicht oder wurde verschoben. Bitte überprüfen Sie die Adresse oder kehren Sie zur Startseite zurück.",
    it: "La pagina cercata non esiste o è stata spostata. Verifichi l'indirizzo o torni alla pagina iniziale.",
    en: "The page you are looking for doesn't exist or has been moved. Please check the address or return to the home page.",
  },
  backToHome: {
    de: "Zur Startseite",
    it: "Alla pagina iniziale",
    en: "Back to home",
  },
  empty: {
    de: "",
    it: "",
    en: "",
  },
};

export declare type TranslatorKeys = keyof typeof generalKeywords;
export type Translatable = CollectionType & {
  name: Translation;
};

const keywords: Keywords = { ...generalKeywords };

export function showTranslated(
  translation: Translation | TranslationCreate,
  lang: LanguageLocaleEnum,
): string {
  switch (lang) {
    case "de":
      return translation.textDE;
    case "en":
      return translation.textEN;
    case "it":
      return translation.textIT;
    default:
      return translation.textDE;
  }
}

export function translate(
  key: TranslatorKeys | Translation | TranslationCreate,
  lang: LanguageLocaleEnum,
  params?: { [T in string]: string },
): string {
  if (typeof key === "object") return showTranslated(key, lang);
  const keyword: Keyword = keywords[key];

  if (typeof keyword === "undefined") {
    return key.toString();
  }
  if (params === undefined) {
    return keyword[lang];
  }
  let text = keyword[lang];
  const paramKeys = Object.keys(params);
  paramKeys.forEach((param) => {
    text = text.replace(param, params[param]);
  });
  return text;
}
